/**
 * Post-checkout RX wizard runtime only (duplicated from rx-config.js so the PDP asset is untouched).
 * Keep in sync with `rx-config.js` when you change prescription/configurator behavior.
 * Loaded by `sections/rx-customer-prescription-completion.liquid` — not used on product templates.
 */

/**
 * Downscales + re-encodes a camera-captured photo as JPEG so it uploads quickly
 * and stays well under the upload size cap, without visibly hurting readability
 * of prescription text. Falls back to the original file on any decode error.
 */
function compressImageFile(
  file,
  { maxDimension = 1800, quality = 0.9, minQuality = 0.6, maxBytes = 2.5 * 1024 * 1024 } = {}
) {
  return new Promise((resolve) => {
    if (!file || !file.type || file.type.indexOf("image/") !== 0) {
      resolve(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width >= height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const tryEncode = (currentQuality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            if (blob.size > maxBytes && currentQuality > minQuality) {
              tryEncode(Math.max(minQuality, currentQuality - 0.1));
              return;
            }
            const baseName = (file.name || "rx-photo").replace(/\.[^.]+$/, "");
            resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
          },
          "image/jpeg",
          currentQuality
        );
      };
      tryEncode(quality);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
}

class RxConfigurator extends HTMLElement {
  constructor() {
    super();
    this.isNavigatingBack = false;
  }
  connectedCallback() {
    new LensTypeSelection();
    this.test = false;
    // Generate a unique session ID for this page/tab instance
    this.sessionId =
      Math.random().toString(36).substr(2, 9) + Date.now().toString(36);

    if (this.test) {
      this.formData = {
        lensType: "Progressive",
        prescription: {
          right: {
            SPH: "-20.0",
            CYL: "-6.0",
            AXI: "0",
          },
          left: {
            SPH: "-20.0",
            CYL: "-6.0",
            AXI: "0",
          },
          ADD: "0.75",
          PD: "54.0",
          isSamePrescription: true,
        },
        lensSpecification: {
          "AR Coating": {
            title: "Super Hydrophobic Anti Reflective",
            price: {
              value: "12900",
              html: "$129.00",
            },
          },
          "Material group": {
            title: "1.67 High-Index",
            price: {
              value: "8000",
              html: "$80.00",
            },
          },
        },
      };
    } else {
      this.formData = {
        lensType: null,
        prescription: {
          right: {
            SPH: "",
            CYL: "",
            AXI: "",
          },
          left: {
            SPH: "",
            CYL: "",
            AXI: "",
          },
          ADD: "",
          PD: "",
          isSamePrescription: false,
        },
        lensSpecification: {},
        pdfUrl: null,
      };
    }
    // Prioritize global product object (has latest data) over section dataset
    if (window.productObj && window.productObj.id) {
      this.product = window.productObj;
    } else {
      this.product = JSON.parse(this.dataset.product);
    }

    this.rules = {};
    this.currentView = "type";
    this.viewsObject = {
      1: {
        container: this.querySelector("#prescriptionTypeView"),
        type: "type",
        step: 1,
        validate: () => this.test || this.selectedType,
      },
      2: {
        container: this.querySelector("#prescriptionMethodView"),
        type: "method",
        step: 2,
        validate: () => this.test || this.selectedMethod,
        skipIf: () => {
          return this.formData.lensType === "Non-prescription" ||
            this.formData.lensType === "Frame only";
        }
      },
      3: {
        container: this.querySelector("#prescriptionFormView"),
        type: "prescription",
        step: 3,
        validate: (showErrors) => this.test || this.validatePrescription(showErrors),
        renderDynamicView: () => this.renderInputsByPrescriptionType(),
        skipIf: () => {
          // NEVER skip if prescription method is "manual", "upload", or "camera" - these require prescription entry
          if (this.formData.prescriptionMethod === "manual" ||
            this.formData.prescriptionMethod === "upload" ||
            this.formData.prescriptionMethod === "camera") {
            console.log(`✅ NOT skipping step 3 (prescription) - prescriptionMethod requires entry: ${this.formData.prescriptionMethod}`);
            return false;
          }

          // Only skip if lens type is Non-prescription or Frame only
          if (this.formData.lensType === "Non-prescription" ||
            this.formData.lensType === "Frame only") {
            console.log(`⏭️ Skipping step 3 (prescription) - lensType: ${this.formData.lensType}`);
            return true;
          }

          // Only skip if prescription method is explicitly "saved" or "later"
          if (this.formData.prescriptionMethod &&
            ["saved", "later"].includes(this.formData.prescriptionMethod)) {
            console.log(`⏭️ Skipping step 3 (prescription) - prescriptionMethod: ${this.formData.prescriptionMethod}`);
            return true;
          }

          // Default: Do NOT skip (show the step)
          // This handles cases where prescriptionMethod is undefined/null
          console.log(`✅ NOT skipping step 3 (prescription) - lensType: ${this.formData.lensType}, prescriptionMethod: ${this.formData.prescriptionMethod || 'undefined'}`);
          return false;
        }
      },
      4: {
        container: this.querySelector("#lensMaterialView"),
        type: "material",
        step: 4,
        validate: (showErrors = false) => {
          if (this.test) return true;
          const isValid = this.validateMaterial();
          // Only show errors when explicitly requested (on Continue button click) AND we're on this step
          if (showErrors && !isValid && this.currentStep === 4) {
            // Find mandatory fields that are not selected
            const materialView = this.querySelector("#lensMaterialView");
            if (materialView) {
              const unselectedMandatory = Array.from(materialView.querySelectorAll("rx-add-on")).filter(
                (item) => {
                  if (item.classList.contains("hidden") || item.closest(".hidden")) return false;
                  if (item.dataset.mandatory !== "true") return false;
                  if (item.includedLensType && item.includedLensType.length > 0) {
                    const included = item.includedLensType.includes(this.formData.lensType);
                    if (!included) return false;
                  }
                  return !item.selectedAddon;
                }
              );

              if (unselectedMandatory.length > 0) {
                const errorMessage = "Please select all required lens options.";
                this.showMaterialError(errorMessage);
              }
            }
          }
          return isValid;
        },
        renderDynamicView: () => this.renderMaterialView(),
      },
      5: {
        container: this.querySelector("#lensBifocalTypeView"),
        type: "bifocalType",
        step: 5,
        validate: (showErrors = false) => {
          if (this.test) return true;
          const isValid = this.validateBifocalType();
          if (showErrors && !isValid) {
            // Bifocal type is required for Bifocal/Progressive lenses
            // Error would be shown if needed, but this step is skipped if not applicable
          }
          return isValid;
        },
        skipIf: () => {
          return this.formData.lensType !== "Bifocal/Progressive";
        }
      },
      6: {
        container: this.querySelector("#lensCoatingView"),
        type: "coating",
        step: 6,
        validate: (showErrors = false) => {
          if (this.test) return true;
          const isValid = this.validateCoating();
          // Only show errors when explicitly requested (on Continue button click) AND we're on this step
          if (showErrors && !isValid && this.currentStep === 6) {
            // Coating validation - check if any selection is made
            const coatingView = this.querySelector("#lensCoatingView");
            if (coatingView) {
              // Check if at least one option is selected
              let hasSelection = false;
              coatingView.querySelectorAll("rx-add-on").forEach((item) => {
                if (item.classList.contains("hidden") || item.closest(".hidden")) return;
                if (item.selectedAddon) {
                  hasSelection = true;
                }
              });

              if (!hasSelection) {
                const errorMessage = "Please select a coating option.";
                this.showCoatingError(errorMessage);
              } else {
                // Check for mandatory selections that are not selected
                const unselectedMandatory = Array.from(coatingView.querySelectorAll("rx-add-on")).filter(
                  (item) => {
                    if (item.classList.contains("hidden") || item.closest(".hidden")) return false;
                    if (item.dataset.mandatory !== "true") return false;
                    return !item.selectedAddon;
                  }
                );

                if (unselectedMandatory.length > 0) {
                  const errorMessage = "Please select a coating option.";
                  this.showCoatingError(errorMessage);
                }
              }
            }
          }
          return isValid;
        },
        renderDynamicView: () => this.renderCoatingView(),
        skipIf: () => {
          // NEVER skip the coating step - it should always be shown
          // The "No Coating" option is always available
          return false;
        },
      },
      7: {
        container: this.querySelector("#lensColorView"),
        type: "color",
        step: 7,
        validate: async (showErrors = false) => {
          var valid = this.test || this.validateLensColor(showErrors);
          if (!valid) return false;
          var confirmed = true;

          // Check if Clear lens is selected for sunglasses
          const lensColorTitle = this.formData?.lensSpecification["Lens Color"]?.title;
          const isSunglasses = document.querySelector('[data-sunglasses-tag="true"]');

          console.log('Lens Color Validation:', {
            showErrors,
            lensColorTitle,
            isSunglasses: !!isSunglasses,
            formData: this.formData?.lensSpecification
          });

          if (
            showErrors &&
            lensColorTitle &&
            lensColorTitle.split("-")[0].trim() === "Clear" &&
            isSunglasses
          ) {
            console.log('Showing confirmation modal for Clear + Sunglasses');
            confirmed = await showConfirmationModal();
            console.log('User confirmed:', confirmed);
          }

          if (!confirmed) return false;

          // Clear error on successful validation
          if (valid) {
            this.clearLensColorError();
          }

          return true;
        },
        renderDynamicView: () => this.renderColorView(),
      },
      8: {
        container: this.querySelector("#lensTintView"),
        type: "tint",
        step: 8,
        validate: (showErrors = false) => {
          if (this.test) return true;
          const isValid = this.validateTint();
          if (showErrors && !isValid) {
            // Tint validation error would be shown if needed
            // This step is typically optional
          }
          return isValid;
        },
        skipIf: () => {
          return !this.formData.lensSpecification["Lens Color"] ||
            this.formData.lensSpecification["Lens Color"]?.title !== "Tint";
        }
      },
      9: {
        container: this.querySelector("#reviewConfirmView"),
        type: "confirm",
        step: 9,
        validate: () => true,
        renderDynamicView: () => this.updateReviewSection(),
      },
    };

    this.currentStep = 1;
    this.selectedType = this.formData.lensType || null;
    this.selectedMethod = null;
    this.selectedBifocalType = null;
    this.selectedTintColor = null;
    this.tintDarkness = 80;
    this.views = {
      type: this.querySelector("#prescriptionTypeView"),
      method: this.querySelector("#prescriptionMethodView"),
      prescription: this.querySelector("#prescriptionFormView"),
      material: this.querySelector("#lensMaterialView"),
      bifocalType: this.querySelector("#lensBifocalTypeView"),
      coating: this.querySelector("#lensCoatingView"),
      color: this.querySelector("#lensColorView"),
      tint: this.querySelector("#lensTintView"),
      confirm: this.querySelector("#reviewConfirmView"),
    };
    this.continueButton = this.querySelector('[data-action="continue"]');
    this.addToCartButton = this.querySelector(
      '[data-action="submit-prescription"], [data-action="add-to-cart"]'
    );
    this.backButton = this.querySelector('[data-action="back"]');
    this.configuratorLoader = document.querySelector("rx-configurator-loader");
    // Continue button is always enabled - errors shown on click
    this.continueButton.disabled = false;

    this.merged = false;

    document.querySelectorAll(".delivery-estimate").forEach((item) => {
      item.innerHTML = this.dataset.shippingLabel;
    });
    this.init();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const maxStep = Object.keys(this.viewsObject).length;
        let n = 1;
        let firstView = this.viewsObject[1];
        while (n <= maxStep) {
          const step = this.viewsObject[n];
          if (!step) break;
          if (typeof step.skipIf !== "function" || !step.skipIf()) {
            firstView = step;
            break;
          }
          n++;
        }

        if (firstView && firstView.container) {
          this.showView(firstView);
          this.positionProgressBar(firstView);
          this.updateProgress({ step: firstView.step });
        }
        const progressBar = this.querySelector(".rx-top-progress-bar");
        if (progressBar) {
          progressBar.style.display = "block";
          progressBar.style.visibility = "visible";
        }
        this.updateToSelectedVariantData();
      });
    });
  }
  init() {
    this.initSelectionComponent();
    this.initMethodSelectionComponent();
    this.initEntryComponent();
    this.initPdfUploadComponent();
    this.initCameraUploadComponent();
    this.initMaterialComponent();
    this.initBifocalTypeComponent();
    this.initCoatingComponent();
    this.initColorComponent();
    this.initTintComponent();
    this.generateRules();

    this.continueButton.addEventListener("click", () => this.handleContinue());
    this.backButton.addEventListener("click", () => this.handleBack());
    this.addToCartButton.addEventListener("click", () =>
      this.handleAddToCart()
    );

    this.attachBackButtonHandlers();
    this.initSelectionDropdown();

    const linkField = document.querySelector("[name='properties[_link_id]']");
    if (linkField) linkField.value = this.generateRandomId();
  }

  initSelectionDropdown() {
    const dropdown = this.querySelector('.selection-summary-dropdown');
    const header = this.querySelector('.selection-summary-header');
    const content = this.querySelector('.selection-summary-content');

    if (header && content) {
      header.addEventListener('click', () => {
        const isOpen = dropdown.classList.contains('open');
        if (isOpen) {
          dropdown.classList.remove('open');
          content.style.display = 'none';
        } else {
          dropdown.classList.add('open');
          content.style.display = 'block';
        }
      });
    }
  }

  updateFooterSelections() {
    const detailsContainer = this.querySelector('#footerSelectionDetails');
    if (!detailsContainer) return;

    const typeItem = detailsContainer.querySelector('[data-selection="type"]');
    if (typeItem) {
      let typeValue = this.formData.lensType || '';
      let typePrice = '';

      if (this.formData.lensSpecification) {
        Object.entries(this.formData.lensSpecification).forEach(([key, data]) => {
          if (key === 'Lens Type' || key.includes('Type')) {
            if (data && data.title) {
              typeValue = data.title;
              typePrice = data.price?.html || '';
            }
          }
        });
      }

      if (typeValue && typeValue !== '') {
        typeItem.style.display = 'flex';
        typeItem.querySelector('.selection-item-value').textContent = typeValue;
        const priceElement = typeItem.querySelector('.selection-item-price');
        if (priceElement) {
          priceElement.textContent = typePrice;
        }
      } else {
        typeItem.style.display = 'none';
        typeItem.querySelector('.selection-item-value').textContent = '';
        const priceElement = typeItem.querySelector('.selection-item-price');
        if (priceElement) {
          priceElement.textContent = '';
        }
      }
    }

    const materialItem = detailsContainer.querySelector('[data-selection="material"]');
    if (materialItem) {
      let mat = null;
      if (this.formData.lensSpecification) {
        Object.entries(this.formData.lensSpecification).forEach(([key, data]) => {
          if (key === 'Lens Material' || key.includes('Material')) {
            mat = data;
          }
        });
      }
      if (mat && mat.title && mat.title !== '-' && mat.title !== '' && mat.title !== 'N/A') {
        materialItem.style.display = 'flex';
        materialItem.querySelector('.selection-item-value').textContent = mat.title;
        materialItem.querySelector('.selection-item-price').textContent = mat.price?.html || '';
      } else {
        materialItem.style.display = 'none';
        materialItem.querySelector('.selection-item-value').textContent = '';
        materialItem.querySelector('.selection-item-price').textContent = '';
      }
    }

    const coatingItem = detailsContainer.querySelector('[data-selection="coating"]');
    if (coatingItem) {
      let coat = null;
      if (this.formData.lensSpecification) {
        Object.entries(this.formData.lensSpecification).forEach(([key, data]) => {
          if (key === 'Anti-Reflective Coating' || key.includes('Coating')) {
            coat = data;
          }
        });
      }
      if (coat && coat.title && coat.title !== '-' && coat.title !== '' && coat.title !== 'N/A') {
        coatingItem.style.display = 'flex';
        coatingItem.querySelector('.selection-item-value').textContent = coat.title;
        coatingItem.querySelector('.selection-item-price').textContent = coat.price?.html || '';
      } else {
        coatingItem.style.display = 'none';
        coatingItem.querySelector('.selection-item-value').textContent = '';
        coatingItem.querySelector('.selection-item-price').textContent = '';
      }
    }

    const colorItem = detailsContainer.querySelector('[data-selection="color"]');
    if (colorItem) {
      if (this.currentStep < 7) {
        colorItem.style.display = 'none';
      } else {
        let color = null;
        if (this.formData.lensSpecification) {
          Object.entries(this.formData.lensSpecification).forEach(([key, data]) => {
            if (key === 'Lens Color' || key === 'Color' || key.includes('Color')) {
              color = data;
            }
          });
        }

        if (color && color.title && color.title !== '-' && color.title !== '' && color.title !== 'N/A') {
          colorItem.style.display = 'flex';
          colorItem.querySelector('.selection-item-value').textContent = color.title;
          colorItem.querySelector('.selection-item-price').textContent = color.price?.html || '';
        } else {
          colorItem.style.display = 'none';
          colorItem.querySelector('.selection-item-value').textContent = '';
          colorItem.querySelector('.selection-item-price').textContent = '';
        }
      }
    }
  }

  attachBackButtonHandlers() {
    const configurator = this;

    if (this._backHandlersAttached) return;
    this._backHandlersAttached = true;

    this._backButtonHandler = (e) => {
      const backButton = e.target.closest('.prescription-back-arrow');
      if (!backButton) return;

      e.preventDefault();
      e.stopPropagation();

      const action = backButton.getAttribute('data-action');

      if (action === 'back') {
        configurator.handleBack();
      } else if (action === 'back-to-product') {
        const loader = document.querySelector('rx-configurator-loader');
        if (loader && loader.hideConfigurator) {
          loader.hideConfigurator();
        }
      } else if (action === 'back-to-lens-type') {
        const step1View = configurator.viewsObject[1];
        if (step1View) {
          configurator.showView(step1View);
        }
      }
    };

    this.addEventListener('click', this._backButtonHandler);
  }

  initSelectionComponent() {
    const typeOptions = this.querySelectorAll('#prescriptionTypeView .prescription-option');
    typeOptions.forEach(option => {
      option.addEventListener('click', () => {
        const selectedType = option.dataset.type;
        if (!selectedType) return;

        this.selectedType = selectedType;
        this.formData.lensType = selectedType;

        typeOptions.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');

        if (this.selectedType === "Non-prescription" || this.selectedType === "Frame only") {
          this.formData.prescription = {
            right: {
              SPH: "",
              CYL: "",
              AXI: "",
            },
            left: {
              SPH: "",
              CYL: "",
              AXI: "",
            },
            ADD: "",
            PD: "",
            isSamePrescription: false,
          };
        }

        this.formData.lensSpecification = {};

        this.updateFooterSelections();
        this.updateContinueButton();
      });
    });

    this.addEventListener("lensTypeSelected", (e) => {
      if (this.test) return;

      const selectedType = e.detail?.type;
      if (!selectedType) return;

      this.selectedType = selectedType;
      this.formData.lensType = selectedType;

      // Update visual selection
      typeOptions.forEach(opt => {
        if (opt.dataset.type === selectedType) {
          opt.classList.add('selected');
        } else {
          opt.classList.remove('selected');
        }
      });

      this.updateFooterSelections();

      if (this.selectedType === "Non-prescription" || this.selectedType === "Frame only") {
        this.formData.prescription = {
          right: {
            SPH: "",
            CYL: "",
            AXI: "",
          },
          left: {
            SPH: "",
            CYL: "",
            AXI: "",
          },
          ADD: "",
          PD: "",
          isSamePrescription: false,
        };
      }

      this.formData.lensSpecification = {};
      this.updateContinueButton();

      setTimeout(() => {
        this.handleContinue();
      }, 300);
    });
  }

  generateRules() {
    this.rulesMetaObject = JSON.parse(this.dataset.rules);
    this.allAvailableMaterials = JSON.parse(this.dataset.allAvailableMaterials);
    this.prescriptionTypes = JSON.parse(this.dataset.prescriptionTypes);
    this.prescriptionTypes.forEach((prescriptionType) => {
      const lowerCasePrescriptionType = prescriptionType.title.toLowerCase();
      this.rules[lowerCasePrescriptionType] = {};
      this.rulesMetaObject.forEach((rule) => {
        if (
          rule.supported_lens_types
            .join(",")
            .toLowerCase()
            .includes(lowerCasePrescriptionType)
        )
          this.rules[lowerCasePrescriptionType][rule.title] = rule;
      });
    });
  }
  validateCorrespondingMaterial() {
    const lowerCasePrescriptionType = this.formData.lensType.toLowerCase();

    this.allAvailableMaterials.forEach((material) => {
      console.log("material");
      console.log(material);
      return;
      const correspondingMaterialRuleForSelectedPrescriptionType =
        this.rules[lowerCasePrescriptionType][material.title];
      if (correspondingMaterialRuleForSelectedPrescriptionType) {
        let validMaterial = true;
        var max_cyl, max_sph, min_sph;
        if (correspondingMaterialRuleForSelectedPrescriptionType.max_cyl)
          max_cyl = parseFloat(
            correspondingMaterialRuleForSelectedPrescriptionType.max_cyl
          );

        if (correspondingMaterialRuleForSelectedPrescriptionType.max_sph)
          max_sph = parseFloat(
            correspondingMaterialRuleForSelectedPrescriptionType.max_sph
          );

        if (correspondingMaterialRuleForSelectedPrescriptionType.min_sph)
          min_sph = parseFloat(
            correspondingMaterialRuleForSelectedPrescriptionType.min_sph
          );
        const { right, left } = this.formData.prescription;
        if (max_cyl && (!right.CYL || max_cyl < parseFloat(right.CYL)))
          validMaterial = false;
        if (max_sph && (!right.SPH || max_sph < parseFloat(right.SPH)))
          validMaterial = false;
        if (min_sph && (!right.SPH || min_sph > parseFloat(right.SPH)))
          validMaterial = false;

        if (max_cyl && (!left.CYL || max_cyl < parseFloat(left.CYL)))
          validMaterial = false;
        if (max_sph && (!left.SPH || max_sph < parseFloat(left.SPH)))
          validMaterial = false;
        if (min_sph && (!left.SPH || min_sph > parseFloat(left.SPH)))
          validMaterial = false;
        const correspondingInput = this.querySelector(
          `input[value="${material.title}"]`
        );
        if (validMaterial) {
          correspondingInput.disabled = false;
          correspondingInput.parentElement.classList.remove("disabled");
        } else {
          correspondingInput.disabled = true;
          correspondingInput.parentElement.classList.add("disabled");
        }
      }
    });
  }

  initMethodSelectionComponent() {
    const methodOptions = this.querySelectorAll(".prescription-method-option");

    methodOptions.forEach((option) => {
      option.addEventListener("click", () => {
        methodOptions.forEach((opt) => opt.classList.remove("selected"));
        option.classList.add("selected");
        this.selectedMethod = option.dataset.method;
        this.formData.prescriptionMethod = option.dataset.method;
        this.updateContinueButton();

        // Open the camera immediately, in the same user gesture as the tap,
        // so mobile browsers don't block the picker once we're past a setTimeout.
        if (option.dataset.method === "camera") {
          const cameraFileInput = this.querySelector("#prescription-camera-file");
          if (cameraFileInput) {
            cameraFileInput.click();
          }
        }

        setTimeout(() => {
          this.handleContinue();
        }, 300);
      });
    });
  }

  initBifocalTypeComponent() {
    this.addEventListener("lensMaterialSelected", (e) => {
      if (this.test) return;
      if (e.detail && Object.keys(e.detail).some(key => key === 'Lens Type')) {
        Object.keys(e.detail).forEach((key) => {
          this.formData.lensSpecification[key] = e.detail[key];
          if (key === 'Lens Type') {
            this.selectedBifocalType = e.detail[key].title;
            this.formData.bifocalType = e.detail[key].title;
          }
        });
        this.updateTotalPrice();
        this.updateContinueButton();
        this.updateFooterSelections();

        if (this.currentStep === 5 && this.validateBifocalType()) {
          setTimeout(() => {
            this.handleContinue();
          }, 300);
        }
      }
    });

    const bifocalView = this.querySelector('#lensBifocalTypeView');
    if (bifocalView) {
      const handleBifocalChange = () => {
        bifocalView.querySelectorAll("rx-add-on").forEach((item) => {
          if (item.classList.contains("hidden") || item.closest(".hidden")) return;
          const checkedInput = item.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const formData = item.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                this.formData.lensSpecification[key] = formData[key];
                if (key === 'Lens Type') {
                  this.selectedBifocalType = formData[key].title;
                  this.formData.bifocalType = formData[key].title;
                }
              });
              this.updateTotalPrice();
              this.updateContinueButton();
              this.updateFooterSelections();

              if (this.currentStep === 5 && this.validateBifocalType()) {
                setTimeout(() => {
                  this.handleContinue();
                }, 300);
              }
            }
          }
        });
      };

      bifocalView.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.checked) {
          setTimeout(handleBifocalChange, 10);
        }
      });

      bifocalView.querySelectorAll('input[type="radio"]').forEach(input => {
        input.addEventListener('change', () => {
          if (input.checked) {
            setTimeout(handleBifocalChange, 10);
          }
        });
      });
    }
  }

  initCoatingComponent() {
    return;
  }

  initColorComponent() {
    return;
  }

  initTintComponent() {
    const tintColorButtons = this.querySelectorAll('#lensTintView .lens-material-color-option');
    tintColorButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.selectedTintColor = button.dataset.label;
        tintColorButtons.forEach(btn => btn.classList.remove('rx-selected_color'));
        button.classList.add('rx-selected_color');
        this.updateTintFormData();
        this.updateContinueButton();
      });
    });

    const darknessSlider = this.querySelector('#tint-darkness');
    if (darknessSlider) {
      darknessSlider.addEventListener('input', (e) => {
        this.tintDarkness = e.target.value;
        this.updateDarknessValue(e.target.value);
        this.updatePresetButtonStates(e.target.value);
        this.updateTintFormData();
      });
    }

    this.initTintPresetButtons();
  }

  initTintPresetButtons() {
    const presetButtons = this.querySelectorAll('.tint-preset-btn');
    presetButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const darkness = parseInt(button.dataset.darkness);
        this.setTintDarkness(darkness);
      });
    });

    const defaultDarkness = this.tintDarkness || 80;
    this.updatePresetButtonStates(defaultDarkness);
  }

  setTintDarkness(darkness) {
    this.tintDarkness = darkness;

    const darknessSlider = this.querySelector('#tint-darkness');
    if (darknessSlider) {
      darknessSlider.value = darkness;
    }

    const allSliders = this.querySelectorAll('.tint-darkness-slider, #tint-darkness');
    allSliders.forEach(slider => {
      slider.value = darkness;
    });

    this.updateDarknessValue(darkness);

    this.updatePresetButtonStates(darkness);

    this.updateTintFormData();
  }

  updateDarknessValue(darkness) {
    const darknessValues = this.querySelectorAll('.darkness-value');
    darknessValues.forEach(element => {
      element.textContent = `${darkness}%`;
    });
  }

  updatePresetButtonStates(darkness) {
    const presetButtons = this.querySelectorAll('.tint-preset-btn');
    presetButtons.forEach(button => {
      const buttonDarkness = parseInt(button.dataset.darkness);
      if (buttonDarkness === parseInt(darkness)) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  updateTintFormData() {
    const tintView = this.querySelector('#lensTintView');
    if (!tintView) return;

    const tintInput = tintView.querySelector('input[type="radio"][name="Tint Color"]');
    if (!tintInput || !this.selectedTintColor) return;

    const colorPicker = tintView.querySelector('color-picker-rx');
    const tintSlider = colorPicker?.querySelector('.tint-darkness-slider, #tint-darkness');
    const darkness = tintSlider ? tintSlider.value : this.tintDarkness || 80;

    let title = `Tint - ${this.selectedTintColor}`;
    if (darkness) {
      title = `${title} - ${darkness}% darkness`;
    }

    const priceValue = parseFloat(tintInput.dataset.addonPrice) || 0;
    const priceHtml = tintInput.dataset.addonPriceHtml || this._rxFormatMoney(priceValue);

    this.formData.lensSpecification['Lens Color'] = {
      title: title,
      price: {
        value: priceValue,
        html: priceHtml
      }
    };

    this.updateFooterSelections();
    this.updateTotalPrice();
  }

  initEntryComponent() {
    // Listen for form events
    this.addEventListener("prescriptionUpdated", (e) => {
      if (this.test) return;
      this.formData.prescription = e.detail;
      this.updateTotalPrice();
      this.updateContinueButton(e.isMerged);
    });
  }

  initMaterialComponent() {
    this.addEventListener("lensMaterialSelected", (e) => {
      if (this.test) return;
      Object.keys(e.detail).forEach((key) => {
        this.formData.lensSpecification[key] = e.detail[key];
      });
      this.updateTotalPrice();
      this.updateContinueButton();
      this.updateFooterSelections();

      // Check if all mandatory material groups are selected
      if (this.currentStep === 4 && this.validateMaterial()) {
        // Auto-advance to next step after a short delay
        setTimeout(() => {
          this.handleContinue();
        }, 300);
      }
    });
    this.addEventListener("lensColorUpdated", (e) => {
      if (this.test) return;
      this.formData.lensSpecification.Color = e.detail.Color;
      this.updateTotalPrice();
      this.updateFooterSelections();
    });

    const materialView = this.querySelector('#lensMaterialView');
    const coatingView = this.querySelector('#lensCoatingView');
    const colorView = this.querySelector('#lensColorView');

    if (materialView) {
      materialView.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.checked) {
          setTimeout(() => {
            const rxAddOn = e.target.closest('rx-add-on');
            if (rxAddOn) {
              const formData = rxAddOn.getSelectedAddOnFormData();
              if (formData) {
                Object.keys(formData).forEach((key) => {
                  this.formData.lensSpecification[key] = formData[key];
                });
                this.updateTotalPrice();
                this.updateFooterSelections();

                // Clear error when user makes a valid selection
                if (this.currentStep === 4) {
                  if (this.validateMaterial()) {
                    this.clearMaterialError();
                    // Auto-advance to next step after a short delay
                    setTimeout(() => {
                      this.handleContinue();
                    }, 300);
                  }
                }
              }
            }
          }, 10);
        }
      });
    }

    if (coatingView) {
      coatingView.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.checked) {
          setTimeout(() => {
            const rxAddOn = e.target.closest('rx-add-on');
            if (rxAddOn) {
              const formData = rxAddOn.getSelectedAddOnFormData();
              if (formData) {
                Object.keys(formData).forEach((key) => {
                  this.formData.lensSpecification[key] = formData[key];
                });
                this.updateTotalPrice();
                this.updateFooterSelections();

                // Uncheck the no-coating checkbox when a regular option is chosen
                const noCoatingCheckbox = coatingView.querySelector('.no-coating-checkbox');
                if (noCoatingCheckbox && e.target.value !== 'No Coating') {
                  noCoatingCheckbox.checked = false;
                }

                // Clear error when user makes a valid selection
                if (this.currentStep === 6) {
                  if (this.validateCoating()) {
                    this.clearCoatingError();
                    // Auto-advance to next step after a short delay
                    setTimeout(() => {
                      this.handleContinue();
                    }, 300);
                  }
                }
              }
            }
          }, 10);
        }
      });

      const noCoatingCheckbox = coatingView.querySelector('.no-coating-checkbox');
      if (noCoatingCheckbox) {
        noCoatingCheckbox.addEventListener('change', (e) => {
          const rxAddOn = coatingView.querySelector('rx-add-on');
          if (!rxAddOn) return;

          const noCoatingInput = rxAddOn.querySelector('input[value="No Coating"]');
          const noCoatingLabel = rxAddOn.querySelector('.no-coating-box-option .lens-material-radio-option');
          if (!noCoatingInput) return;

          if (e.target.checked) {
            // User checked "No Coating"
            noCoatingInput.checked = true;
            if (noCoatingLabel) {
              rxAddOn.selectedAddon = noCoatingLabel;
            }

            const coatingGroupName = noCoatingInput.name;
            this.formData.lensSpecification[coatingGroupName] = {
              title: 'No Coating',
              price: { value: 0, html: '$0.00' },
            };
            this.updateTotalPrice();
            this.updateFooterSelections();

            if (this.currentStep === 6) {
              this.clearCoatingError();
              setTimeout(() => {
                this.handleContinue();
              }, 300);
            }
          } else {
            // User unchecked "No Coating" - clear the selection
            noCoatingInput.checked = false;
            if (noCoatingLabel) {
              rxAddOn.selectedAddon = null;
            }

            const coatingGroupName = noCoatingInput.name;
            if (this.formData.lensSpecification && this.formData.lensSpecification[coatingGroupName]) {
              delete this.formData.lensSpecification[coatingGroupName];
            }
            this.updateTotalPrice();
            this.updateFooterSelections();
          }
        });
      }
    }

    if (colorView) {
      colorView.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.checked) {
          setTimeout(() => {
            const rxAddOn = e.target.closest('rx-add-on');
            if (rxAddOn) {
              // Check if there's a color picker for this option
              const colorPicker = e.target
                .closest('.lens-material-radio-option-container')
                ?.querySelector('color-picker-rx');

              // If color picker exists, ensure it's open and a color is selected
              if (colorPicker && colorPicker.classList.contains('hidden')) {
                colorPicker.open();
              }

              // Get formData - this will auto-select a color if needed (single swatch or first available)
              const formData = rxAddOn.getSelectedAddOnFormData();
              if (formData) {
                Object.keys(formData).forEach((key) => {
                  this.formData.lensSpecification[key] = formData[key];
                });
                this.updateTotalPrice();
                this.updateFooterSelections();

                // Update continue button state
                this.updateContinueButton();

                if (this.currentStep === 7 && !colorPicker) {
                  setTimeout(() => {
                    this.handleContinue();
                  }, 300);
                } else if (colorPicker) {
                  // Clear error when option is selected and color is auto-selected
                  this.clearLensColorError();

                  // If single swatch, auto-advance after a short delay
                  const colorOptions = colorPicker.querySelectorAll(".lens-material-color-option");
                  if (colorOptions.length === 1) {
                    setTimeout(() => {
                      this.handleContinue();
                    }, 300);
                  }
                }
              }
            }
          }, 50);
        }
      });

      // Add listener for swatch selection to trigger auto-advance
      colorView.addEventListener('click', (e) => {
        if (e.target.classList.contains('lens-material-color-option')) {
          setTimeout(() => {
            // Update continue button state after swatch selection
            this.updateContinueButton();
            this.clearLensColorError();
          }, 100);

          setTimeout(() => {
            if (this.currentStep === 7 && this.validateLensColor()) {
              this.handleContinue();
            }
          }, 300);
        }
      });
    }
  }
  generateLinkIdFilename(originalFilename) {
    if (!this.linkId) {
      this.linkId = this.generateRandomId();
    }
    return `${this.linkId}-${originalFilename}`;
  }
  isAllowedPrescriptionFile(file) {
    if (!file) return false;
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/heic",
      "image/heif",
    ];
    if (allowedTypes.includes(file.type)) return true;
    const name = (file.name || "").toLowerCase();
    return [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".heif"].some((ext) =>
      name.endsWith(ext)
    );
  }

  initPdfUploadComponent() {
    const pdfUploadForm = this.querySelector("#prescription-pdf-upload-form");
    const pdfFileInput = this.querySelector("#prescription-pdf-file");
    const pdfFileLabel = this.querySelector(".pdf-file-label");
    const pdfFileText = this.querySelector(".pdf-file-text");
    const progressDiv = this.querySelector(".pdf-upload-progress");
    const successDiv = this.querySelector(".pdf-upload-success");
    const errorDiv = this.querySelector(".pdf-upload-error");

    if (!pdfUploadForm) return;

    // Initialize visual state based on existing PDF
    if (this.formData.pdfUrl) {
      this.updatePrescriptionFormVisualState(true);
      if (successDiv) {
        successDiv.style.display = "flex";
      }
    }

    const uploadDifferentBtn = this.querySelector(".pdf-upload-different");
    if (uploadDifferentBtn) {
      uploadDifferentBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.formData.prescriptionMethod === "camera") {
          this.querySelector("#prescription-camera-file")?.click();
        } else {
          pdfFileInput.click();
        }
      });
    }

    // Handle file input change
    pdfFileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!this.isAllowedPrescriptionFile(file)) {
          errorDiv.style.display = "flex";
          const errorText = errorDiv.querySelector(".pdf-upload-error-text");
          if (errorText) {
            errorText.textContent =
              "Unsupported file type. Please upload a PDF or image (JPG, PNG, HEIC).";
          }
          pdfFileInput.value = "";
          return;
        }
        // If we already have a PDF, we're replacing it
        if (this.formData.pdfUrl) {
          this.formData.pdfUrl = null;
          this.formData.pdfFileId = null;
          this.updatePrescriptionFormVisualState(false);
        }
        const uploadInputWrapper = this.querySelector(".pdf-upload-input-wrapper");
        if (uploadInputWrapper) {
          uploadInputWrapper.style.display = "block";
        }
        const successDiv = this.querySelector(".pdf-upload-success");
        if (successDiv) {
          successDiv.style.display = "none";
        }
        this.uploadPrescriptionPdf(file);
      }
    });

    // Handle drag and drop
    pdfFileLabel.addEventListener("dragover", (e) => {
      e.preventDefault();
      pdfFileLabel.classList.add("drag-over");
    });

    pdfFileLabel.addEventListener("dragleave", () => {
      pdfFileLabel.classList.remove("drag-over");
    });

    pdfFileLabel.addEventListener("drop", (e) => {
      e.preventDefault();
      pdfFileLabel.classList.remove("drag-over");
      const file = e.dataTransfer.files[0];
      if (file && this.isAllowedPrescriptionFile(file)) {
        // If we already have a PDF, we're replacing it
        if (this.formData.pdfUrl) {
          this.formData.pdfUrl = null;
          this.formData.pdfFileId = null;
          this.updatePrescriptionFormVisualState(false);
        }
        pdfFileInput.files = e.dataTransfer.files;
        this.uploadPrescriptionPdf(file);
      }
    });
  }

  getActiveUploadInputWrapper() {
    return this.formData.prescriptionMethod === "camera"
      ? this.querySelector(".camera-upload-input-wrapper")
      : this.querySelector(".pdf-upload-input-wrapper");
  }

  initCameraUploadComponent() {
    const cameraFileInput = this.querySelector("#prescription-camera-file");
    const cameraOpenButton = this.querySelector(".camera-open-button");
    const errorDiv = this.querySelector(".pdf-upload-error");

    if (!cameraFileInput) return;

    if (cameraOpenButton) {
      cameraOpenButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        cameraFileInput.click();
      });
    }

    cameraFileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!this.isAllowedPrescriptionFile(file)) {
        if (errorDiv) {
          errorDiv.style.display = "flex";
          const errorText = errorDiv.querySelector(".pdf-upload-error-text");
          if (errorText) {
            errorText.textContent = "Unsupported photo format. Please try again.";
          }
        }
        cameraFileInput.value = "";
        return;
      }

      if (this.formData.pdfUrl) {
        this.formData.pdfUrl = null;
        this.formData.pdfFileId = null;
        this.updatePrescriptionFormVisualState(false);
      }

      const successDiv = this.querySelector(".pdf-upload-success");
      if (successDiv) {
        successDiv.style.display = "none";
      }
      const uploadInputWrapper = this.getActiveUploadInputWrapper();
      if (uploadInputWrapper) {
        uploadInputWrapper.style.display = "flex";
      }

      compressImageFile(file)
        .then((compressedFile) => this.uploadPrescriptionPdf(compressedFile))
        .catch(() => this.uploadPrescriptionPdf(file));
    });
  }

  uploadPrescriptionPdf(file) {
    const progressDiv = this.querySelector(".pdf-upload-progress");
    const successDiv = this.querySelector(".pdf-upload-success");
    const errorDiv = this.querySelector(".pdf-upload-error");
    const uploadInputWrapper = this.getActiveUploadInputWrapper();

    successDiv.style.display = "none";
    errorDiv.style.display = "none";
    if (uploadInputWrapper) {
      uploadInputWrapper.style.display = "flex";
    }

    // Quick file size check (prevent slow uploads)
    const maxSize = 3 * 1024 * 1024; // 3MB limit for speed
    if (file.size > maxSize) {
      errorDiv.style.display = "flex";
      const errorText = errorDiv.querySelector(".pdf-upload-error-text");
      if (errorText) {
        errorText.textContent = "File too large. Please use a file under 3MB.";
      }
      return;
    }

    progressDiv.style.display = "flex";
    // Generate filename with link ID prefix using helper function
    const linkIdFilename = this.generateLinkIdFilename(file.name);
    // Convert file to Base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64File = reader.result.split(",")[1]; // Remove base64 prefix

      // Replace with your Google Apps Script Web App URL
      const appsScriptUrl =
        "https://script.google.com/macros/s/AKfycbyb34Wo0K2-KrD_jo2Ym0mJnXmFGZ0ZCkEvjxh4iFnU2jy2JDz1haaPLwjUeqKULyVubA/exec";
      // Create upload with timeout for faster failure detection
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout - mobile camera photos over cellular can be slow to upload

      fetch(appsScriptUrl, {
        method: "POST",
        redirect: "follow",
        signal: controller.signal,
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify({
          filename: linkIdFilename,
          mimeType: file.type,
          data: base64File,
        }),
      })
        .then((response) => {
          console.log("Response status:", response.status);
          console.log("Response headers:", response.headers);

          // Always try to get the response as text first to see what we're getting
          return response.text().then((text) => {
            console.log("Raw response text:", text);

            // Try to parse as JSON
            try {
              const data = JSON.parse(text);
              console.log("Parsed JSON response:", data);
              return data;
            } catch (e) {
              console.error("Failed to parse JSON response:", e);
              console.error("Response text was:", text);
              throw new Error(
                "Server returned invalid JSON: " + text.substring(0, 200)
              );
            }
          });
        })
        .then((data) => {
          console.log("Upload response data:", data);
          console.log("data.success:", data.success);
          console.log("data.fileUrl:", data.fileUrl);
          console.log("data.error:", data.error);

          clearTimeout(timeoutId); // Clear timeout on success
          progressDiv.style.display = "none";
          if (data.success) {
            this.formData.pdfUrl = data.fileUrl || `FILE_ID:${data.fileId}`;
            this.formData.pdfFileId = data.fileId;

            // Hide the upload input wrapper and show success
            const uploadInputWrapper = this.getActiveUploadInputWrapper();
            if (uploadInputWrapper) {
              uploadInputWrapper.style.display = "none";
            }
            successDiv.style.display = "flex";

            const fileNameEl = successDiv.querySelector(".pdf-file-name");
            if (fileNameEl) {
              fileNameEl.textContent = this.generateLinkIdFilename(file.name);
            }

            this.updateContinueButton();
            this.updatePrescriptionFormVisualState(true);

            if (data.fileUrl) {
              console.log("PDF uploaded successfully with URL:", data.fileUrl);
            } else {
              console.log(
                "PDF uploaded successfully, URL processing in background. File ID:",
                data.fileId
              );
            }
          } else {
            throw new Error(data.error || "Upload failed");
          }
        })
        .catch((error) => {
          clearTimeout(timeoutId); // Clear timeout on error
          progressDiv.style.display = "none";
          errorDiv.style.display = "flex";
          console.error("Error uploading PDF:", error);

          // Show more detailed error in the UI
          const errorText = errorDiv.querySelector(".pdf-upload-error-text");
          if (errorText) {
            if (error.name === "AbortError") {
              errorText.textContent =
                "Upload timeout - please try a smaller file or check your connection.";
            } else {
              errorText.textContent = "Upload failed: " + error.message;
            }
          }

          // Add retry button to error div if it doesn't exist
          if (!errorDiv.querySelector(".pdf-retry-button")) {
            const retryButton = document.createElement("button");
            retryButton.className = "pdf-retry-button";
            retryButton.textContent = "Try Again";
            retryButton.addEventListener("click", (e) => {
              e.stopPropagation();
              e.preventDefault();
              errorDiv.style.display = "none";
              // Reset file input safely
              if (pdfFileInput) {
                pdfFileInput.value = "";
              }
              if (pdfFileText) {
                pdfFileText.textContent = "Upload your RX";
              }
            });
            errorDiv.appendChild(retryButton);
          }
        });
    };

    reader.readAsDataURL(file);
  }
  removePrescriptionPdf(event = null) {
    console.log("removePrescriptionPdf called");

    try {
      // Prevent any event bubbling or unwanted side effects
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      // Clear the uploaded PDF data
      this.formData.pdfUrl = null;
      this.formData.pdfFileId = null;

      console.log("PDF data cleared");

      // Reset the file input without triggering change events
      const pdfFileInput = this.querySelector("#prescription-pdf-file");
      const pdfFileText = this.querySelector(".pdf-file-text");
      const pdfFileLabel = this.querySelector(".pdf-file-label");

      if (pdfFileInput) {
        // Temporarily remove event listeners to prevent unwanted triggers
        const tempClone = pdfFileInput.cloneNode(true);
        pdfFileInput.parentNode.replaceChild(tempClone, pdfFileInput);
        tempClone.value = "";

        // Re-add the file input change event listener
        tempClone.addEventListener("change", (e) => {
          const file = e.target.files[0];
          if (file) {
            if (!this.isAllowedPrescriptionFile(file)) {
              tempClone.value = "";
              return;
            }
            // If we already have a PDF, we're replacing it
            if (this.formData.pdfUrl) {
              this.formData.pdfUrl = null;
              this.formData.pdfFileId = null;
              this.updatePrescriptionFormVisualState(false);
            }
            this.uploadPrescriptionPdf(file);
          }
        });

        // Re-add drag and drop functionality
        if (pdfFileLabel) {
          pdfFileLabel.addEventListener("dragover", (e) => {
            e.preventDefault();
            pdfFileLabel.classList.add("drag-over");
          });

          pdfFileLabel.addEventListener("dragleave", () => {
            pdfFileLabel.classList.remove("drag-over");
          });

          pdfFileLabel.addEventListener("drop", (e) => {
            e.preventDefault();
            pdfFileLabel.classList.remove("drag-over");
            const file = e.dataTransfer.files[0];
            if (file && this.isAllowedPrescriptionFile(file)) {
              // If we already have a PDF, we're replacing it
              if (this.formData.pdfUrl) {
                this.formData.pdfUrl = null;
                this.formData.pdfFileId = null;
                this.updatePrescriptionFormVisualState(false);
              }
              tempClone.files = e.dataTransfer.files;
              this.uploadPrescriptionPdf(file);
            }
          });
        }
      }

      const uploadInputWrapper = this.querySelector(".pdf-upload-input-wrapper");
      if (uploadInputWrapper) {
        uploadInputWrapper.style.display = "block";
      }

      const successDiv = this.querySelector(".pdf-upload-success");
      if (successDiv) {
        successDiv.style.display = "none";
      }

      // Hide any error divs
      const errorDiv = this.querySelector(".pdf-upload-error");
      if (errorDiv) {
        errorDiv.style.display = "none";
      }

      // Update visual state
      this.updatePrescriptionFormVisualState(false);

      // Update continue button state with a small delay to prevent issues
      setTimeout(() => {
        this.updateContinueButton();
        console.log("removePrescriptionPdf completed successfully");
      }, 100);
    } catch (error) {
      console.error("Error in removePrescriptionPdf:", error);
      // Even if there's an error, make sure the UI is in a consistent state
      this.updatePrescriptionFormVisualState(false);
      // Update continue button state
      setTimeout(() => {
        this.updateContinueButton();
      }, 100);
    }
  }

  updatePrescriptionFormVisualState(pdfUploaded) {
    const prescriptionForm = this.querySelector(".prescription-form");
    const prescriptionTable = this.querySelector(".prescription-table");
    const prescriptionAdditionContainer = this.querySelector(
      ".prescription-table-addition-container"
    );
    const pdSection = this.querySelector(".prescription-table-pd");

    if (pdfUploaded) {
      // Add visual indicator that form is now optional
      if (
        prescriptionForm &&
        !prescriptionForm.querySelector(".pdf-uploaded-notice")
      ) {
        const notice = document.createElement("div");
        notice.className = "pdf-uploaded-notice";
        notice.innerHTML = `
          <div class="pdf-uploaded-notice-content">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" fill="#059669"/>
              <path d="M6.5 10l2.5 2.5L15 6.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>RX uploaded successfully! Only PD (Pupillary Distance) is still required below.</span>
          </div>
        `;
        prescriptionForm.insertBefore(notice, prescriptionTable);
      }

      // Make most form fields visually optional but keep PD section at full opacity
      if (prescriptionTable) {
        prescriptionTable.style.opacity = "0.7";
      }
      if (prescriptionAdditionContainer) {
        // Dim the ADD section but keep PD section visible
        const addSection = prescriptionAdditionContainer.querySelector(
          ".prescription-table-addition"
        );
        if (addSection) {
          addSection.style.opacity = "0.7";
        }
        // Keep PD section at full opacity since it's still required and highlight it
        if (pdSection) {
          pdSection.style.opacity = "1";
          pdSection.classList.add("highlight-required");
        }
      }
    } else {
      // Remove the notice and restore normal appearance
      const notice = prescriptionForm?.querySelector(".pdf-uploaded-notice");
      if (notice) {
        notice.remove();
      }

      if (prescriptionTable) {
        prescriptionTable.style.opacity = "1";
      }
      if (prescriptionAdditionContainer) {
        prescriptionAdditionContainer.style.opacity = "1";
        // Also restore ADD section
        const addSection = prescriptionAdditionContainer.querySelector(
          ".prescription-table-addition"
        );
        if (addSection) {
          addSection.style.opacity = "1";
        }
      }
      if (pdSection) {
        pdSection.style.opacity = "1";
        pdSection.classList.remove("highlight-required");
      }
    }
  }

  generateRandomId(length = 10) {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const chars = letters + numbers;

    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Include timestamp and session ID for better uniqueness across tabs/pages
    const timestamp = Date.now().toString(36);
    return `${result}_${timestamp}_${this.sessionId}`;
  }

  async updateCartNotesWithLatestState(newOrderNote, retryCount = 0) {
    const maxRetries = 3;
    const retryDelay = 100 * Math.pow(2, retryCount); // Exponential backoff: 100ms, 200ms, 400ms

    try {
      console.log(
        `Updating cart notes (attempt ${retryCount + 1}/${maxRetries + 1})`
      );

      // Always fetch the latest cart state right before updating
      const cartResponse = await fetch(window.Shopify.routes.root + "cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }

      const cartData = await cartResponse.json();
      let existingNotes = cartData.note || "";

      // Check if this note already exists (duplicate prevention)
      // Look for the specific Link ID pattern to avoid false positives
      const linkIdMatch = newOrderNote.match(/Link ID: ([^)]+)/);
      if (linkIdMatch && existingNotes.includes(linkIdMatch[1])) {
        console.log(
          `Order note with Link ID ${linkIdMatch[1]} already exists in cart, skipping duplicate`
        );
        return Promise.resolve();
      }

      // Append the new order note to existing notes
      let updatedNotes = existingNotes;

      // Add separator if there are existing notes
      if (existingNotes && existingNotes.trim() !== "") {
        updatedNotes += "\n\n---\n\n";
      }

      // Add the new order note
      updatedNotes += newOrderNote;

      console.log("Updating cart with notes:", updatedNotes);

      // Update cart with appended notes
      const updateResponse = await fetch(
        window.Shopify.routes.root + "cart/update.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            note: updatedNotes,
          }),
        }
      );

      if (!updateResponse.ok) {
        throw new Error(`Failed to update cart: ${updateResponse.status}`);
      }

      console.log("Cart notes updated successfully");
      return Promise.resolve();
    } catch (error) {
      console.error(
        `Error updating cart notes (attempt ${retryCount + 1}):`,
        error
      );

      if (retryCount < maxRetries) {
        console.log(`Retrying in ${retryDelay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return this.updateCartNotesWithLatestState(
          newOrderNote,
          retryCount + 1
        );
      } else {
        console.error("Max retries reached. Cart note update failed.");
        // Don't throw error - allow the flow to continue even if notes update fails
        return Promise.resolve();
      }
    }
  }
  handleAddToCart() {
    const selectedVariant = this.getCurrentSelectedVariant();

    // Validate that a variant is selected
    if (!selectedVariant || !selectedVariant.id) {
      console.error("❌ No variant selected. Cannot add to cart.");
      alert("Please select a product variant before adding to cart.");
      return;
    }

    this.addToCartButton
      .querySelector(".loading-spinner")
      .classList.remove("hidden");
    this.addToCartButton
      .querySelector(".add-to-cart-button-text")
      .classList.add("hidden");
    let specifications = {};
    let amountToBeAdded = 0;
    const lensSpecifications = Object.keys(
      this.formData.lensSpecification
    ).forEach((item, index) => {
      specifications[item] = this.formData.lensSpecification[item].title;
      amountToBeAdded += parseFloat(
        this.formData.lensSpecification[item].price.value
      );
    });

    if (this.formData.prescription.isAddPrism && this.formData.prescription.prism) {
      amountToBeAdded += 1900;
    }

    // let link_id = this.generateRandomId();
    let link_id = this.linkId || this.generateRandomId();

    // Get optician comment from textarea
    const opticianCommentElement = this.querySelector("#optician-comment");
    const opticianComment = opticianCommentElement ? opticianCommentElement.value.trim() : "";

    const prescription = {
      lens_type: this.formData.lensType,
      SPH_right: this.formData.prescription.right.SPH,
      CYL_right: this.formData.prescription.right.CYL,
      AXI_right: this.formData.prescription.right.AXI,
      SPH_left: this.formData.prescription.left.SPH,
      CYL_left: this.formData.prescription.left.CYL,
      AXI_left: this.formData.prescription.left.AXI,
      PD: this.formData.prescription.isSplitPd ? "" : this.formData.prescription.PD,
      PD_right: this.formData.prescription.isSplitPd ? this.formData.prescription.PD_right : "",
      PD_left: this.formData.prescription.isSplitPd ? this.formData.prescription.PD_left : "",
      ADD: this.formData.prescription.ADD,
      ...specifications,
    };

    if (this.formData.prescription.isAddPrism && this.formData.prescription.prism) {
      prescription.prism_vertical_right = this.formData.prescription.prism.right.vertical;
      prescription.prism_vertical_base_right = this.formData.prescription.prism.right.verticalBase;
      prescription.prism_horizontal_right = this.formData.prescription.prism.right.horizontal;
      prescription.prism_horizontal_base_right = this.formData.prescription.prism.right.horizontalBase;
      prescription.prism_vertical_left = this.formData.prescription.prism.left.vertical;
      prescription.prism_vertical_base_left = this.formData.prescription.prism.left.verticalBase;
      prescription.prism_horizontal_left = this.formData.prescription.prism.left.horizontal;
      prescription.prism_horizontal_base_left = this.formData.prescription.prism.left.horizontalBase;
      prescription.add_prism = "true";
      prescription.prism_price = "19.00";

      const formatPrismValue = (value) => {
        if (!value || value === "n/a" || value === "") return value;
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        if (num % 1 === 0) {
          return num.toString();
        }
        return num.toString().replace(/\.?0+$/, '');
      };

      const prism = this.formData.prescription.prism;
      const prismParts = [];

      if (prism.right.vertical && prism.right.vertical !== "n/a" && prism.right.vertical !== "" &&
        prism.right.verticalBase && prism.right.verticalBase !== "n/a" && prism.right.verticalBase !== "") {
        const baseLower = prism.right.verticalBase.toLowerCase();
        const formattedValue = formatPrismValue(prism.right.vertical);
        prismParts.push(`OD ΔV: ${formattedValue} ${baseLower}`);
      }
      if (prism.right.horizontal && prism.right.horizontal !== "n/a" && prism.right.horizontal !== "" &&
        prism.right.horizontalBase && prism.right.horizontalBase !== "n/a" && prism.right.horizontalBase !== "") {
        const baseLower = prism.right.horizontalBase.toLowerCase();
        const formattedValue = formatPrismValue(prism.right.horizontal);
        prismParts.push(`OD ΔH: ${formattedValue} ${baseLower}`);
      }

      if (prism.left.vertical && prism.left.vertical !== "n/a" && prism.left.vertical !== "" &&
        prism.left.verticalBase && prism.left.verticalBase !== "n/a" && prism.left.verticalBase !== "") {
        const baseLower = prism.left.verticalBase.toLowerCase();
        const formattedValue = formatPrismValue(prism.left.vertical);
        prismParts.push(`OS ΔV: ${formattedValue} ${baseLower}`);
      }
      if (prism.left.horizontal && prism.left.horizontal !== "n/a" && prism.left.horizontal !== "" &&
        prism.left.horizontalBase && prism.left.horizontalBase !== "n/a" && prism.left.horizontalBase !== "") {
        const baseLower = prism.left.horizontalBase.toLowerCase();
        const formattedValue = formatPrismValue(prism.left.horizontal);
        prismParts.push(`OS ΔH: ${formattedValue} ${baseLower}`);
      }

      if (prismParts.length > 0) {
        prescription.prism = prismParts.join(", ");
      }
    }
    this.linkId = link_id;

    let newPrescriptionNote = "";
    if (this.formData.pdfUrl) {
      const productTitle = this.product.title;
      newPrescriptionNote = `${productTitle} - Prescription PDF (Link ID: ${link_id}):`;
      newPrescriptionNote += `\nPrescription PDF Url: ${this.formData.pdfUrl}`;
      if (this.formData.pdfFileId) {
        newPrescriptionNote += `\nPrescription PDF File ID: ${this.formData.pdfFileId}`;
      }
    }

    // Add note for "Send it later" orders
    if (this.formData.prescriptionMethod === "later") {
      const productTitle = this.product.title;
      const sendLaterNote = `CONTACT CUSTOMER FOR PRESCRIPTION\n${productTitle} (Link ID: ${link_id})\nCustomer selected "Send it later" - prescription details required before fulfillment.`;
      if (newPrescriptionNote) {
        newPrescriptionNote += `\n\n${sendLaterNote}`;
      } else {
        newPrescriptionNote = sendLaterNote;
      }
    }

    if (opticianComment) {
      if (newPrescriptionNote) {
        newPrescriptionNote += `\n\nCustomer Note: ${opticianComment}`;
      } else {
        newPrescriptionNote = `Customer Note: ${opticianComment}`;
      }
    }
    // First, get current cart state to append notes instead of overwriting
    const getCurrentCartAndAddItem = async () => {
      try {
        // Fetch current cart
        const cartResponse = await fetch(
          window.Shopify.routes.root + "cart.js"
        );
        const currentCart = await cartResponse.json();
        let existingNotes = currentCart.note || "";

        // Append new prescription note to existing notes
        let finalOrderNote = "";
        if (existingNotes.trim() !== "") {
          finalOrderNote = existingNotes;
          if (newPrescriptionNote.trim() !== "") {
            finalOrderNote += "\n\n---\n\n" + newPrescriptionNote;
          }
        } else {
          finalOrderNote = newPrescriptionNote;
        }

        // Set cart attributes + note via cart/update.js FIRST (most reliable method)
        // cart/update.js is the ONLY guaranteed way to persist cart-level attributes.
        // cart/add.js does NOT reliably support top-level `attributes` in its body.
        // Flow condition: order.customAttributes.key equals "prescription_status" AND value equals "send_later"
        if (this.formData.prescriptionMethod === "later") {
          const existingAttributes = currentCart.attributes || {};
          const cartUpdatePayload = {
            note: finalOrderNote,
            attributes: {
              ...existingAttributes,
              "_needs_prescription": "true",
              "prescription_status": "send_later", // Primary: matches Flow condition (no underscore)
              "_prescription_status": "send_later", // Backup: with underscore prefix
            },
          };
          console.log("✅ Setting cart attributes + note via cart/update.js BEFORE add");
          await fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(cartUpdatePayload),
          });
        }

        let formData = {
          items: [
            {
              id: selectedVariant.id,
              // id: 42085097734228,
              quantity: 1,
              properties: {
                // _devmode: "true",
                _link_id: link_id,
                _lens_variant_id: 42085097734228,
                _frame_id: selectedVariant.id,
                _is_prescription_order: "true",
                _prescription: JSON.stringify(prescription),
                _newAmount: amountToBeAdded,
                _newTitle: `${this.product.title} custom lenses`,
                _prescription_pdf_url: this.formData.pdfUrl || "",
                _prescription_pdf_file_id: this.formData.pdfFileId || "",
                _product_name: this.product.title,
                // _order_timestamp: new Date().toISOString(),
                _prescription_type: this.formData.lensType || "Unknown",
                _has_prescription_pdf: this.formData.pdfUrl ? "true" : "false",
                _discount_tag:
                  this.product.tags.find((tag) =>
                    tag.startsWith("feed_import_discounted_")
                  ) || "",
                _has_discount: this.product.tags.some((tag) =>
                  tag.startsWith("feed_import_discounted_")
                )
                  ? "true"
                  : "false",
                _bundle_role: "frame",
                _original_frame_price_cents: String(selectedVariant.price),
                _original_lens_price_cents: String(amountToBeAdded),
                _optician_comment: opticianComment || "",
                _contact_customer_for_prescription: this.formData.prescriptionMethod === "later" ? "true" : "false",
                // Note: prescription_status is set in cart attributes via cart/update.js above
              },
            },
          ],
          note: finalOrderNote,
        };

        return fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });
      } catch (error) {
        console.error("Error getting current cart:", error);
        // Fallback path: initial cart fetch failed, but still set prescription attributes
        // Use cart/update.js directly — this is the reliable setter for cart-level attributes
        if (this.formData.prescriptionMethod === "later") {
          try {
            console.log("✅ Fallback: Setting cart attributes via cart/update.js");
            await fetch(window.Shopify.routes.root + "cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                note: newPrescriptionNote,
                attributes: {
                  "_needs_prescription": "true",
                  "prescription_status": "send_later", // Primary: matches Flow condition (no underscore)
                  "_prescription_status": "send_later", // Backup: with underscore prefix
                },
              }),
            });
          } catch (updateError) {
            console.error("❌ Fallback cart/update.js also failed:", updateError);
          }
        }
        let formData = {
          items: [
            {
              id: selectedVariant.id,
              quantity: 1,
              properties: {
                _link_id: link_id,
                _lens_variant_id: 42085097734228,
                _is_prescription_order: "true",
                _prescription: JSON.stringify(prescription),
                _newAmount: amountToBeAdded,
                _newTitle: `${this.product.title} custom lenses`,
                _prescription_pdf_url: this.formData.pdfUrl || "",
                _prescription_pdf_file_id: this.formData.pdfFileId || "",
                _product_name: this.product.title,
                // _order_timestamp: new Date().toISOString(),
                _prescription_type: this.formData.lensType || "Unknown",
                _has_prescription_pdf: this.formData.pdfUrl ? "true" : "false",
                _discount_tag:
                  this.product.tags.find((tag) =>
                    tag.startsWith("feed_import_discounted_")
                  ) || "",
                _has_discount: this.product.tags.some((tag) =>
                  tag.startsWith("feed_import_discounted_")
                )
                  ? "true"
                  : "false",
                _bundle_role: "frame",
                _original_frame_price_cents: String(selectedVariant.price),
                _original_lens_price_cents: String(amountToBeAdded),
                _optician_comment: opticianComment || "",
                _contact_customer_for_prescription: this.formData.prescriptionMethod === "later" ? "true" : "false",
              },
            },
          ],
          note: newPrescriptionNote,
        };

        return fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });
      }
    };

    getCurrentCartAndAddItem()
      .then((response) => {
        if (!response.ok) {
          return response.text().then(text => {
            console.error("Add to cart failed:", text);
            throw new Error(`Add to cart failed: ${response.status} ${response.statusText}`);
          });
        }
        return response.json();
      })
      .then(async (data) => {
        // Reinforcement: verify prescription_status was persisted, re-set if missing.
        // The primary set was done via cart/update.js BEFORE cart/add.js above.
        // This guard catches any edge case where cart attributes were cleared or not persisted.
        if (this.formData.prescriptionMethod === "later") {
          try {
            const currentCartResponse = await fetch(window.Shopify.routes.root + "cart.js");
            const currentCart = await currentCartResponse.json();
            const existingAttributes = currentCart.attributes || {};

            if (existingAttributes["prescription_status"] !== "send_later") {
              console.warn("⚠️ prescription_status missing after add — re-setting via cart/update.js");
              await fetch(window.Shopify.routes.root + "cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  attributes: {
                    ...existingAttributes,
                    "_needs_prescription": "true",
                    "prescription_status": "send_later", // Primary: matches Flow condition (no underscore)
                    "_prescription_status": "send_later", // Backup: with underscore prefix
                  },
                }),
              });
              console.log("✅ prescription_status re-set successfully");
            } else {
              console.log("✅ prescription_status confirmed present in cart");
            }
          } catch (error) {
            console.warn("Failed to verify/reinforce cart attributes:", error);
          }
        }

        // Handle different response formats from cart/add.js
        // New format with items array might return different structure
        // If response is successful (no error thrown), proceed even if structure is different
        console.log("Add to cart response:", data);

        // Try to extract variant info from response
        // The response might be the item itself or contain items array
        let variantData = data;
        if (data && Array.isArray(data.items) && data.items.length > 0) {
          variantData = data.items[0];
        } else if (data && data.item) {
          variantData = data.item;
        }

        // Fire Shopify pixel event only if we have the required data
        if (variantData && variantData.variant_id) {
          window.Shopify.analytics.publish('product_added_to_cart', {
            cartLine: {
              cost: {
                totalAmount: {
                  amount: (variantData.price || selectedVariant.price) / 100,
                  currencyCode: window.Shopify.currency.active
                }
              },
              merchandise: {
                id: variantData.variant_id,
                image: {
                  src: variantData.image || selectedVariant.featured_image?.src || this.product.featured_image
                },
                price: {
                  amount: (variantData.price || selectedVariant.price) / 100,
                  currencyCode: window.Shopify.currency.active
                },
                product: {
                  id: variantData.product_id || this.product.id,
                  title: variantData.product_title || this.product.title,
                  vendor: variantData.vendor || this.product.vendor,
                  type: variantData.product_type || this.product.type,
                  untranslatedTitle: variantData.product_title || this.product.title,
                  url: variantData.url || this.product.url
                },
                sku: variantData.sku || selectedVariant.sku,
                title: variantData.variant_title || selectedVariant.title,
                untranslatedTitle: variantData.variant_title || selectedVariant.title
              },
              quantity: variantData.quantity || 1
            }
          });
        } else {
          // If we don't have variant data, still proceed but log a warning
          console.warn("Could not extract variant data from response, but cart add was successful:", data);
        }

        // After adding to cart, update cart notes with PDF URL
        if (this.formData.pdfUrl) {
          return this.updateCartNotesWithLatestState(newPrescriptionNote);
        }
        return Promise.resolve();
      })
      .finally(() => {
        this.addToCartButton
          .querySelector(".loading-spinner")
          .classList.add("hidden");
        this.addToCartButton
          .querySelector(".add-to-cart-button-text")
          .classList.remove("hidden");

        $("body").trigger("added.ajaxProduct");
        this.configuratorLoader.hideConfigurator();
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
        alert(`Failed to add item to cart: ${error.message || "Unknown error"}. Please try again.`);
        // Re-enable the button on error
        this.addToCartButton
          .querySelector(".loading-spinner")
          .classList.add("hidden");
        this.addToCartButton
          .querySelector(".add-to-cart-button-text")
          .classList.remove("hidden");
      });
  }
  async handleContinue() {
    var currentStep = this.viewsObject[this.currentStep];

    if (!currentStep) {
      console.error(`❌ Current step ${this.currentStep} not found in viewsObject`);
      return;
    }

    // Clear ALL errors before validating current step
    // This ensures no errors from previous steps persist when clicking Continue
    this.clearPrescriptionError();
    this.clearMaterialError();
    this.clearCoatingError();
    this.clearLensColorError();

    const valid = await currentStep.validate(true);
    if (!valid) {
      console.log(`❌ Validation failed for step ${this.currentStep} (${currentStep.type})`);
      return;
    }

    var nextStepNumber = this.currentStep + 1;
    var nextStep = this.viewsObject[nextStepNumber];
    var skipCount = 0;
    const maxSteps = Object.keys(this.viewsObject).length;

    // Skip steps that should be skipped, with safety check to prevent infinite loops
    while (nextStep && this.shouldSkipStep(nextStep)) {
      console.log(`⏭️ Skipping step ${nextStep.step} (${nextStep.type})`);
      nextStepNumber++;
      nextStep = this.viewsObject[nextStepNumber];
      skipCount++;

      // Safety check: prevent infinite loop
      if (skipCount > maxSteps || nextStepNumber > maxSteps) {
        console.error(`❌ Infinite loop detected when skipping steps from step ${this.currentStep}`);
        break;
      }
    }

    if (nextStep) {
      console.log(`✅ Navigating to step ${nextStep.step} (${nextStep.type})`);
      // Ensure container exists before showing
      if (!nextStep.container) {
        console.error(`❌ Container not found for step ${nextStep.step} (${nextStep.type})`);
        // Try to re-query the container
        if (nextStep.step === 6) {
          nextStep.container = this.querySelector("#lensCoatingView");
          if (!nextStep.container) {
            console.error(`❌ lensCoatingView container still not found`);
            return;
          }
        }
      }
      // showView() will call renderDynamicView() internally
      // This ensures the container is visible before filtering options
      this.showView(nextStep);
    } else {
      // Expected when currentStep is the last step (review/confirm) — there's
      // nothing after it to navigate to.
    }
  }

  shouldSkipStep(step) {
    if (step.skipIf && typeof step.skipIf === 'function') {
      const shouldSkip = step.skipIf();
      if (step.step === 3) {
        console.log(`🔍 Checking if step 3 (prescription) should be skipped:`, shouldSkip, {
          lensType: this.formData.lensType,
          prescriptionMethod: this.formData.prescriptionMethod
        });
      }
      if (step.step === 6) {
        console.log(`🔍 Checking if step 6 (coating) should be skipped:`, shouldSkip);
      }
      return shouldSkip;
    }
    if (step.step === 3) {
      console.log(`✅ Step 3 (prescription) has no skipIf - will not be skipped`);
    }
    if (step.step === 6) {
      console.log(`✅ Step 6 (coating) has no skipIf - will not be skipped`);
    }
    return false;
  }

  handleBack() {
    // Prevent multiple simultaneous back navigations
    if (this._isHandlingBack) {
      console.log(`⚠️ Back navigation already in progress, ignoring duplicate call`);
      return;
    }
    this._isHandlingBack = true;

    this.isNavigatingBack = true;
    var previousStepNumber = this.currentStep - 1;
    var previousStep = this.viewsObject[previousStepNumber];

    console.log(`🔙 handleBack called from step ${this.currentStep}, previousStepNumber: ${previousStepNumber}, lensType: ${this.formData.lensType}, prescriptionMethod: ${this.formData.prescriptionMethod}`);

    // Special handling for going back from step 3 (prescription form)
    // If user reached step 3, they must have selected a method in step 2 (unless step 2 was skipped)
    // IMPORTANT: If step 2 was skipped (Non-prescription/Frame only), step 3 should also be skipped
    // So if we're on step 3, step 2 MUST have been shown, and we should ALWAYS go back to step 2
    if (this.currentStep === 3 && previousStep && previousStep.step === 2) {
      // Check if step 2 should actually be skipped (Non-prescription or Frame only)
      const shouldSkipStep2 = this.shouldSkipStep(previousStep);
      console.log(`🔍 Step 3 → Step 2: shouldSkipStep2 = ${shouldSkipStep2}, lensType = ${this.formData.lensType}`);

      if (shouldSkipStep2) {
        // This is an edge case - if step 2 should be skipped, step 3 should also be skipped
        // But if we're here, something went wrong. Go to step 1 as fallback.
        console.log(`⚠️ Step 2 should be skipped, but we're on step 3 - this shouldn't happen. Going to step 1.`);
        previousStepNumber = 1;
        previousStep = this.viewsObject[previousStepNumber];
      } else {
        // Step 2 should be shown - ALWAYS go back to step 2
        // Even if prescriptionMethod is not set, step 2 was shown, so go back to it
        console.log(`✅ Going back to step 2 (method selection) - prescriptionMethod: ${this.formData.prescriptionMethod || 'not set'}`);
        // Force previousStepNumber to 2 to ensure we go to step 2
        previousStepNumber = 2;
        previousStep = this.viewsObject[previousStepNumber];

        // Double-check that step 2 shouldn't be skipped
        if (this.shouldSkipStep(previousStep)) {
          console.error(`❌ ERROR: Step 2 should be skipped but we're trying to go to it from step 3. This is a logic error.`);
          // Fallback to step 1
          previousStepNumber = 1;
          previousStep = this.viewsObject[previousStepNumber];
        }
      }
    } else {
      // For all other steps, use normal skip logic to find the previous non-skipped step
      // This handles cases like:
      // - Step 4 → Step 3 (might be skipped for Non-prescription)
      // - Step 5 → Step 4 (step 5 only shows for Bifocal/Progressive)
      // - Step 6 → Step 5 or 4 (step 5 might be skipped)
      // - Step 7 → Step 6 (step 6 never skipped)
      // - Step 8 → Step 7 or 6 (step 8 might be skipped)
      // - Step 9 → Step 8 or 7 (step 8 might be skipped)
      while (previousStep && this.shouldSkipStep(previousStep)) {
        console.log(`⏭️ Skipping step ${previousStep.step} (${previousStep.type}) when going back from step ${this.currentStep}`);
        previousStepNumber--;
        previousStep = this.viewsObject[previousStepNumber];

        // Safety check: prevent infinite loop and ensure we don't go below step 1
        if (previousStepNumber < 1) {
          console.error(`❌ Reached step 0 or below when going back from step ${this.currentStep}`);
          previousStepNumber = 1;
          previousStep = this.viewsObject[previousStepNumber];
          break;
        }
      }
    }

    if (previousStep && previousStep.container) {
      console.log(`✅ Going back to step ${previousStep.step} (${previousStep.type}) from step ${this.currentStep}`);

      // Clear the current step's selections before going back (so user has to select again)
      this.clearCurrentStepSelections(this.currentStep);

      // Clear formData and uncheck inputs for steps beyond the step we're going back to
      this.clearFormDataBeyondStep(previousStepNumber);
      this.showView(previousStep);
    } else {
      console.error(`❌ Cannot go back from step ${this.currentStep}: previous step not found or has no container`);
    }

    setTimeout(() => {
      this.isNavigatingBack = false;
      this._isHandlingBack = false;
    }, 100);
  }

  clearCurrentStepSelections(stepNumber) {
    // Clear selections for the current step we're leaving
    const stepViewMap = {
      4: { view: '#lensMaterialView', field: 'Lens Material' },
      5: { view: '#lensBifocalTypeView', field: 'Lens Type' },
      6: { view: '#lensCoatingView', field: 'Anti-Reflective Coating' },
      7: { view: '#lensColorView', field: 'Lens Color' },
      8: { view: '#lensTintView', field: null }
    };

    const stepInfo = stepViewMap[stepNumber];
    if (stepInfo && stepInfo.view) {
      const view = this.querySelector(stepInfo.view);
      if (view) {
        // Clear formData for this step
        if (stepInfo.field && this.formData.lensSpecification) {
          // Clear the specific field
          if (this.formData.lensSpecification[stepInfo.field]) {
            delete this.formData.lensSpecification[stepInfo.field];
          }
          // Also clear any related fields (e.g., "Color" if field is "Lens Color")
          Object.keys(this.formData.lensSpecification).forEach(key => {
            if (stepInfo.field === 'Lens Color' && (key === 'Color' || key.includes('Color'))) {
              delete this.formData.lensSpecification[key];
            } else if (stepInfo.field === 'Lens Material' && key.includes('Material')) {
              delete this.formData.lensSpecification[key];
            } else if (stepInfo.field === 'Anti-Reflective Coating' && (key === 'Anti-Reflective Coating' || key.includes('Coating'))) {
              delete this.formData.lensSpecification[key];
            }
          });
        }

        if (stepNumber === 5) {
          this.selectedBifocalType = null;
          this.formData.bifocalType = null;
        }
        if (stepNumber === 8) {
          this.selectedTintColor = null;
          this.tintDarkness = 80;
        }

        // Uncheck all radio inputs in this step
        view.querySelectorAll('input[type="radio"]').forEach(input => {
          if (input.checked) {
            input.checked = false;
          }
          const rxAddOn = input.closest('rx-add-on');
          if (rxAddOn && rxAddOn.selectedAddon) {
            rxAddOn.selectedAddon = null;
          }
          const optionContainer = input.closest('.lens-material-radio-option-container');
          if (optionContainer) {
            const label = optionContainer.querySelector('.lens-material-radio-option');
            if (label) {
              label.classList.remove('selected');
              label.classList.remove('rx-selected');
            }
          }
        });

        // Clear selectedAddon for all rx-add-on elements
        view.querySelectorAll('rx-add-on').forEach(rxAddOn => {
          if (rxAddOn.selectedAddon) {
            rxAddOn.selectedAddon = null;
          }
        });

        // Clear color picker selections
        const colorPicker = view.querySelector('color-picker-rx');
        if (colorPicker) {
          colorPicker.selectedColorButton = null;
          colorPicker.querySelectorAll('.lens-material-color-option').forEach(btn => {
            btn.classList.remove('rx-selected_color');
          });
          const tintSlider = colorPicker.querySelector('.tint-darkness-slider, #tint-darkness');
          if (tintSlider) {
            tintSlider.value = 80;
            const darknessValue = colorPicker.querySelector('.darkness-value');
            if (darknessValue) {
              darknessValue.textContent = '80%';
            }
          }
        }
      }
    }

    // Update UI after clearing
    this.updateTotalPrice();
    this.updateFooterSelections();
    this.updateContinueButton();
  }

  clearFormDataBeyondStep(stepNumber) {
    const fieldsToClear = [];

    if (stepNumber < 4) {
      fieldsToClear.push('Lens Material');
    }
    if (stepNumber < 5) {
      fieldsToClear.push('Lens Type');
      this.selectedBifocalType = null;
      this.formData.bifocalType = null;
    }
    if (stepNumber < 6) {
      fieldsToClear.push('Anti-Reflective Coating');
    }
    if (stepNumber < 7) {
      fieldsToClear.push('Lens Color');
    }
    if (stepNumber < 8) {
      this.selectedTintColor = null;
      this.tintDarkness = 80;
    }

    if (this.formData.lensSpecification) {
      fieldsToClear.forEach(field => {
        if (this.formData.lensSpecification[field]) {
          delete this.formData.lensSpecification[field];
        }
      });

      Object.keys(this.formData.lensSpecification).forEach(key => {
        if (key.includes('Material') && stepNumber < 4) {
          delete this.formData.lensSpecification[key];
        } else if ((key === 'Lens Type' || key.includes('Type')) && stepNumber < 5) {
          delete this.formData.lensSpecification[key];
        } else if ((key === 'Anti-Reflective Coating' || key.includes('Coating')) && stepNumber < 6) {
          delete this.formData.lensSpecification[key];
        } else if ((key === 'Lens Color' || key === 'Color' || key.includes('Color')) && stepNumber < 7) {
          delete this.formData.lensSpecification[key];
        }
      });
    }

    this.uncheckInputsBeyondStep(stepNumber);
    this.updateTotalPrice();
    this.updateFooterSelections();
  }

  uncheckInputsBeyondStep(stepNumber) {
    const stepViewMap = {
      4: '#lensMaterialView',
      5: '#lensBifocalTypeView',
      6: '#lensCoatingView',
      7: '#lensColorView',
      8: '#lensTintView'
    };

    Object.keys(stepViewMap).forEach(step => {
      if (parseInt(step) > stepNumber) {
        const view = this.querySelector(stepViewMap[step]);
        if (view) {
          view.querySelectorAll('input[type="radio"]').forEach(input => {
            if (input.checked) {
              input.checked = false;
            }
            const rxAddOn = input.closest('rx-add-on');
            if (rxAddOn && rxAddOn.selectedAddon) {
              rxAddOn.selectedAddon = null;
            }
            const optionContainer = input.closest('.lens-material-radio-option-container');
            if (optionContainer) {
              const label = optionContainer.querySelector('.lens-material-radio-option');
              if (label) {
                label.classList.remove('selected');
                label.classList.remove('rx-selected');
              }
            }
            const prescriptionOption = input.closest('.prescription-option');
            if (prescriptionOption) {
              prescriptionOption.classList.remove('selected');
            }
          });

          view.querySelectorAll('rx-add-on').forEach(rxAddOn => {
            if (rxAddOn.selectedAddon) {
              rxAddOn.selectedAddon = null;
            }
          });

          const colorPicker = view.querySelector('color-picker-rx');
          if (colorPicker) {
            colorPicker.selectedColorButton = null;
            const tintSlider = colorPicker.querySelector('.tint-slider, #tint-darkness');
            if (tintSlider) {
              tintSlider.value = 80;
              const darknessValue = colorPicker.querySelector('.darkness-value');
              if (darknessValue) {
                darknessValue.textContent = '80%';
              }
            }
          }
        }
      }
    });
  }

  syncPreSelectedValues(viewObject) {
    if (!viewObject || !viewObject.container) return;

    const step = viewObject.step;
    console.log('🔍 syncPreSelectedValues - Step:', step, 'isNavigatingBack:', this.isNavigatingBack);

    if (step === 4) {
      if (this.isNavigatingBack) {
        const materialView = viewObject.container;
        materialView.querySelectorAll("rx-add-on").forEach((item) => {
          if (item.classList.contains("hidden") || item.closest(".hidden")) return;
          const checkedInput = item.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const formData = item.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                this.formData.lensSpecification[key] = formData[key];
              });
            }
          }
        });
      }
    } else if (step === 5) {
      if (this.isNavigatingBack) {
        const bifocalView = viewObject.container;
        bifocalView.querySelectorAll("rx-add-on").forEach((item) => {
          if (item.classList.contains("hidden") || item.closest(".hidden")) return;
          const checkedInput = item.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const formData = item.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                this.formData.lensSpecification[key] = formData[key];
                if (key === 'Lens Type') {
                  this.selectedBifocalType = formData[key].title;
                  this.formData.bifocalType = formData[key].title;
                }
              });
              this.updateTotalPrice();
              this.updateFooterSelections();
            }
          }
        });
      }
    } else if (step === 6) {
      if (this.isNavigatingBack) {
        const coatingView = viewObject.container;
        coatingView.querySelectorAll("rx-add-on").forEach((item) => {
          if (item.classList.contains("hidden") || item.closest(".hidden")) return;
          const checkedInput = item.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const formData = item.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                this.formData.lensSpecification[key] = formData[key];
              });
            }
          }
        });
      }
    } else if (step === 7) {
      if (this.isNavigatingBack) {
        const colorView = viewObject.container;
        colorView.querySelectorAll("rx-add-on").forEach((item) => {
          if (item.classList.contains("hidden") || item.closest(".hidden")) return;
          const checkedInput = item.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const formData = item.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                this.formData.lensSpecification[key] = formData[key];
              });
            }
          }
        });
      }
    } else if (step === 3) {
      // Restore prescription form values when navigating back to step 3
      this.restorePrescriptionFormValues();
    }

    this.updateTotalPrice();
    this.updateFooterSelections();
  }

  restorePrescriptionFormValues() {
    const prescriptionEntry = this.querySelector("rx-prescription");
    if (!prescriptionEntry || !this.formData.prescription) return;

    const prescription = this.formData.prescription;

    // Restore SPH, CYL, AXI values
    if (prescription.right) {
      if (prescriptionEntry.rightEyeSelects && prescriptionEntry.rightEyeSelects[0]) {
        this.setDropdownValue(prescriptionEntry.rightEyeSelects[0], prescription.right.SPH || "");
      }
      if (prescriptionEntry.rightEyeSelects && prescriptionEntry.rightEyeSelects[1]) {
        this.setDropdownValue(prescriptionEntry.rightEyeSelects[1], prescription.right.CYL || "");
      }
      if (prescriptionEntry.rightEyeSelects && prescriptionEntry.rightEyeSelects[2]) {
        this.setDropdownValue(prescriptionEntry.rightEyeSelects[2], prescription.right.AXI || "");
      }
    }

    if (prescription.left) {
      if (prescriptionEntry.leftEyeSelects && prescriptionEntry.leftEyeSelects[0]) {
        this.setDropdownValue(prescriptionEntry.leftEyeSelects[0], prescription.left.SPH || "");
      }
      if (prescriptionEntry.leftEyeSelects && prescriptionEntry.leftEyeSelects[1]) {
        this.setDropdownValue(prescriptionEntry.leftEyeSelects[1], prescription.left.CYL || "");
      }
      if (prescriptionEntry.leftEyeSelects && prescriptionEntry.leftEyeSelects[2]) {
        this.setDropdownValue(prescriptionEntry.leftEyeSelects[2], prescription.left.AXI || "");
      }
    }

    // Restore ADD
    if (prescriptionEntry.restSelects && prescriptionEntry.restSelects[0]) {
      this.setDropdownValue(prescriptionEntry.restSelects[0], prescription.ADD || "");
    }

    // Restore PD (single or split)
    if (prescription.isSplitPd) {
      if (prescriptionEntry.splitPdCheckbox) {
        prescriptionEntry.splitPdCheckbox.checked = true;
        if (prescriptionEntry.pdRight) {
          this.setDropdownValue(prescriptionEntry.pdRight, prescription.PD_right || "");
        }
        if (prescriptionEntry.pdLeft) {
          this.setDropdownValue(prescriptionEntry.pdLeft, prescription.PD_left || "");
        }
      }
    } else {
      if (prescriptionEntry.splitPdCheckbox) {
        prescriptionEntry.splitPdCheckbox.checked = false;
      }
      if (prescriptionEntry.pdSingle) {
        this.setDropdownValue(prescriptionEntry.pdSingle, prescription.PD || "");
      }
    }

    // Restore merge checkbox
    if (prescriptionEntry.mergeCheckbox) {
      prescriptionEntry.mergeCheckbox.checked = prescription.isSamePrescription || false;
      if (prescription.isSamePrescription) {
        prescriptionEntry.merged = true;
        prescriptionEntry.handlePrescriptionMerge(true);
      }
    }

    // Restore Add Prism checkbox and values
    if (prescriptionEntry.addPrismCheckbox) {
      prescriptionEntry.addPrismCheckbox.checked = prescription.isAddPrism || false;
      prescriptionEntry.handleAddPrismToggle(prescription.isAddPrism || false);

      if (prescription.isAddPrism && prescription.prism) {
        const prism = prescription.prism;

        // Restore right eye prism values
        if (prism.right) {
          if (prescriptionEntry.prismVerticalRight) {
            this.setDropdownValue(prescriptionEntry.prismVerticalRight, prism.right.vertical || "n/a");
          }
          if (prescriptionEntry.prismVerticalBaseRight) {
            this.setDropdownValue(prescriptionEntry.prismVerticalBaseRight, prism.right.verticalBase || "n/a");
          }
          if (prescriptionEntry.prismHorizontalRight) {
            this.setDropdownValue(prescriptionEntry.prismHorizontalRight, prism.right.horizontal || "n/a");
          }
          if (prescriptionEntry.prismHorizontalBaseRight) {
            this.setDropdownValue(prescriptionEntry.prismHorizontalBaseRight, prism.right.horizontalBase || "n/a");
          }
        }

        // Restore left eye prism values
        if (prism.left) {
          if (prescriptionEntry.prismVerticalLeft) {
            this.setDropdownValue(prescriptionEntry.prismVerticalLeft, prism.left.vertical || "n/a");
          }
          if (prescriptionEntry.prismVerticalBaseLeft) {
            this.setDropdownValue(prescriptionEntry.prismVerticalBaseLeft, prism.left.verticalBase || "n/a");
          }
          if (prescriptionEntry.prismHorizontalLeft) {
            this.setDropdownValue(prescriptionEntry.prismHorizontalLeft, prism.left.horizontal || "n/a");
          }
          if (prescriptionEntry.prismHorizontalBaseLeft) {
            this.setDropdownValue(prescriptionEntry.prismHorizontalBaseLeft, prism.left.horizontalBase || "n/a");
          }
        }

        // Re-initialize prism field handlers to update base direction field states
        // This must be done after setting values to ensure base direction fields are enabled/disabled correctly
        setTimeout(() => {
          if (prescriptionEntry.initPrismFieldHandlers) {
            prescriptionEntry.initPrismFieldHandlers();
          }
        }, 0);
      }
    }

    // Trigger update to sync form data
    if (prescriptionEntry.handlePrescriptionUpdate) {
      prescriptionEntry.handlePrescriptionUpdate();
    }
  }

  setDropdownValue(el, value) {
    if (!el || !value) return;

    if (el.tagName === "SELECT") {
      // Standard select element
      el.value = value;
      el.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      // Custom dropdown - update hidden input and selected text
      const hiddenInput = el.querySelector('input[type="hidden"]');
      const selected = el.querySelector(".custom-dropdown-selected");

      if (hiddenInput) {
        hiddenInput.value = value;
      }

      if (selected) {
        // Find the option with matching data-value and update selected text
        const options = el.querySelectorAll(".custom-dropdown-option");
        options.forEach((option) => {
          if (option.getAttribute("data-value") === value) {
            selected.textContent = option.textContent;
          }
        });
      }

      el.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  validateBifocalType() {
    if (this.formData.lensType !== "Bifocal/Progressive") return true;
    return !!this.selectedBifocalType;
  }

  validateCoating() {
    const coatingView = this.querySelector("#lensCoatingView");
    if (!coatingView) return false;

    // Check if at least one coating option is selected
    // Even if all options are non-mandatory, user must make a selection
    let hasSelection = false;
    let allValid = true;

    coatingView.querySelectorAll("rx-add-on").forEach((item) => {
      if (item.classList.contains("hidden") || item.closest(".hidden")) return;

      // Check if this item has a selection
      if (item.selectedAddon) {
        hasSelection = true;
      }

      // Validate the item (for mandatory checks)
      allValid = allValid && item.validateAddOn();
    });

    // Require at least one selection AND all validations to pass
    return hasSelection && allValid;
  }

  validateLensColor(showErrors = false) {
    console.log('🎨 validateLensColor called, step:', this.currentStep, 'showErrors:', showErrors);
    let canPass = true;
    let errorMessage = '';
    const colorView = this.querySelector("#lensColorView");
    if (!colorView) return false;

    colorView.querySelectorAll("rx-add-on").forEach((item) => {
      if (item.classList.contains("hidden") || item.closest(".hidden")) return;

      const itemValid = item.validateAddOn();
      console.log('🎨 Color item validation:', itemValid, item);

      if (!itemValid) {
        // Check if Clear is selected - Clear doesn't need color swatch
        if (item.selectedAddon) {
          const input = item.selectedAddon.querySelector("input");
          const selectedAddonTitle = input?.value || input?.dataset?.addonTitle || '';
          const isClearLens = selectedAddonTitle && selectedAddonTitle.trim().toLowerCase() === 'clear';

          if (isClearLens) {
            console.log('🎨 Clear lens detected - skipping error message');
            // Clear lens is valid, don't set error message
            return;
          }
        }

        if (!item.selectedAddon) {
          errorMessage = 'Please select a lens color option';
        } else {
          const colorPicker = item.selectedAddon
            .closest(".lens-material-radio-option-container")
            ?.querySelector("color-picker-rx");
          if (colorPicker) {
            errorMessage = 'Please select a color swatch for your lens';
          }
        }
      }

      canPass = canPass && itemValid;
    });

    console.log('🎨 validateLensColor result:', canPass);

    // Only show errors when explicitly requested (on Continue button click) AND we're on step 7
    if (showErrors && this.currentStep === 7) {
      this.clearLensColorError();
      if (!canPass && errorMessage) {
        this.showLensColorError(errorMessage);
      }
    }

    return canPass;
  }

  showLensColorError(message) {
    // Only show error if we're on step 7 (lens color step)
    // This prevents errors from showing on other steps
    if (this.currentStep !== 7) {
      console.warn('⚠️ Attempted to show lens color error but not on step 7. Current step:', this.currentStep);
      return;
    }

    const colorView = this.querySelector("#lensColorView");
    if (!colorView) return;

    const buttonContainer = colorView.querySelector('.button-container') ||
      this.querySelector('.prescription-entry-footer .button-container');

    if (!buttonContainer) return;

    let errorDiv = buttonContainer.querySelector('.lens-color-validation-error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'lens-color-validation-error';
      errorDiv.style.cssText = `
        color: #DC2626;
        font-size: 14px;
        font-weight: 500;
        padding: 12px 16px;
        background-color: #FEE2E2;
        border: 1px solid #DC2626;
        border-radius: 8px;
        margin-bottom: 16px;
        display: block;
        width: 100%;
        box-sizing: border-box;
        text-align: left;
      `;
      buttonContainer.insertBefore(errorDiv, buttonContainer.firstChild);
    }

    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
  }

  clearLensColorError() {
    const errorDiv = this.querySelector('.lens-color-validation-error');
    if (errorDiv) {
      errorDiv.remove();
    }
  }

  validateTint() {
    return !!(this.selectedTintColor && this.tintDarkness);
  }

  renderCoatingView() {
    const currentLensType = this.formData?.lensType;
    const coatingView = this.querySelector("#lensCoatingView");
    if (!coatingView) return;

    // Filter coating options based on included_lens_type
    // Note: "No Coating" is always available via the text link, so all rx-add-on elements
    // can be hidden if they don't match the current lens type
    coatingView.querySelectorAll("rx-add-on").forEach((item) => {
      if (!item.dataset.includedLensType) {
        item.classList.remove("hidden");
        return;
      }

      if (!currentLensType) {
        item.classList.remove("hidden");
        return;
      }

      try {
        const parsedIncludedLensType = JSON.parse(item.dataset.includedLensType);
        if (Array.isArray(parsedIncludedLensType) && parsedIncludedLensType.includes(currentLensType)) {
          item.classList.remove("hidden");
        } else {
          item.classList.add("hidden");
        }
      } catch (e) {
        console.warn("Error parsing includedLensType in coating view:", e);
        item.classList.remove("hidden");
      }
    });
  }

  renderColorView() {
    return;
  }

  showView(viewObject) {
    // Clear ALL errors FIRST when showing a new view (prevents errors from previous steps showing)
    // This must happen IMMEDIATELY before anything else to prevent errors from showing on initial load
    // IMPORTANT: Errors should NEVER be shown automatically - only when user clicks Continue
    this.clearPrescriptionError();
    this.clearMaterialError();
    this.clearCoatingError();
    this.clearLensColorError();

    // Double-check: Ensure no error elements exist in the DOM for this step
    if (viewObject.step === 4) {
      // Force clear material error one more time after a brief delay to catch any async issues
      setTimeout(() => {
        this.clearMaterialError();
      }, 50);
    }
    if (viewObject.step === 6) {
      // Force clear coating error one more time after a brief delay to catch any async issues
      setTimeout(() => {
        this.clearCoatingError();
      }, 50);
    }

    Object.values(this.viewsObject).forEach((view) => {
      if (view.container) {
        view.container.classList.add("hidden");
      }
    });

    // Ensure container exists, especially for step 6 (coating)
    if (!viewObject.container && viewObject.step === 6) {
      viewObject.container = this.querySelector("#lensCoatingView");
      if (!viewObject.container) {
        console.error(`❌ lensCoatingView container not found when trying to show step 6`);
        return;
      }
    }

    if (viewObject.container) {
      viewObject.container.classList.remove("hidden");
    } else {
      console.error(`❌ Container not found for step ${viewObject.step} (${viewObject.type})`);
    }
    this.currentView = viewObject.type;
    this.currentStep = viewObject.step;

    this.attachBackButtonHandlers();

    if (viewObject.renderDynamicView && typeof viewObject.renderDynamicView === 'function') {
      viewObject.renderDynamicView();
      // After rendering, ensure no errors are shown (they should only appear on Continue click)
      if (viewObject.step === 4) {
        this.clearMaterialError();
      }
      if (viewObject.step === 6) {
        this.clearCoatingError();
        // Additional clear after a micro-delay to catch any async rendering issues
        setTimeout(() => {
          this.clearCoatingError();
        }, 10);
      }
    }

    this.syncPreSelectedValues(viewObject);

    // Update no-coating checkbox state when showing step 6
    if (viewObject.step === 6) {
      const coatingView = viewObject.container;
      const noCoatingCheckbox = coatingView?.querySelector('.no-coating-checkbox');
      if (noCoatingCheckbox) {
        const rxAddOn = coatingView.querySelector('rx-add-on');
        const noCoatingInput = rxAddOn?.querySelector('input[value="No Coating"]');
        if (noCoatingInput && noCoatingInput.checked) {
          noCoatingCheckbox.checked = true;
        } else {
          noCoatingCheckbox.checked = false;
        }
      }
    }

    // Final safety check: Clear errors one more time after all rendering is complete
    if (viewObject.step === 6) {
      setTimeout(() => {
        this.clearCoatingError();
      }, 100);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.positionProgressBar(viewObject);
      });
    });

    const buttonContainer = this.querySelector(
      ".prescription-entry-footer .button-container"
    );
    if (viewObject.step === 1) {
      this.backButton.classList.add("hidden");
      this.addToCartButton.classList.add("hidden");
      buttonContainer.classList.remove("half-width");
    } else {
      const isFinalStep =
        Object.keys(this.viewsObject).length == viewObject.step;
      if (isFinalStep) {
        this.addToCartButton.classList.remove("hidden");
        this.continueButton.classList.add("hidden");
      } else {
        this.backButton.classList.remove("hidden");
        this.addToCartButton.classList.add("hidden");
        this.continueButton.classList.remove("hidden");
        buttonContainer.classList.add("half-width");
      }
    }

    this.updateProgress(viewObject);
    this.updateContinueButton();
    this.updateFooterSelections();
  }

  positionProgressBar(viewObject) {
    const progressBar = this.querySelector('.rx-top-progress-bar');
    if (!progressBar || !viewObject.container) {
      return;
    }

    const wasHidden = viewObject.container.classList.contains('hidden');
    if (wasHidden) {
      viewObject.container.classList.remove('hidden');
    }

    const header = viewObject.container.querySelector('.prescription-header-with-back');
    if (!header) {
      if (wasHidden) {
        viewObject.container.classList.add('hidden');
      }
      return;
    }

    if (header.nextElementSibling === progressBar) {
      if (wasHidden) {
        viewObject.container.classList.add('hidden');
      }
      return;
    }

    if (progressBar.parentNode) {
      progressBar.remove();
    }

    if (!header.parentNode) {
      if (wasHidden) {
        viewObject.container.classList.add('hidden');
      }
      return;
    }

    try {
      header.insertAdjacentElement('afterend', progressBar);
    } catch (e) {
      const nextSibling = header.nextSibling;
      if (nextSibling) {
        header.parentNode.insertBefore(progressBar, nextSibling);
      } else {
        header.parentNode.appendChild(progressBar);
      }
    }

    if (wasHidden) {
      viewObject.container.classList.add('hidden');
    }
  }
  renderInputsByPrescriptionType() {
    const prescriptionTable = this.querySelector(".prescription-table"); // SPH, CYL, AXI fields
    const prescriptionAdditionContainer = this.querySelector(".prescription-table-addition-container"); // ADD field container
    const addSection = this.querySelector(".prescription-table-addition"); // ADD field
    const pdSection = this.querySelector(".prescription-table-pd"); // PD section
    const prismSection = this.querySelector(".prescription-table-prism"); // Prism section
    const samePrescriptionCheckbox = this.querySelector("#same-prescription-checkbox");
    const checkboxContainer = samePrescriptionCheckbox?.closest('.checkbox-container');

    // Handle ADD field visibility based on lens type (only for manual entry)
    if (this.formData.prescriptionMethod !== "upload" && this.formData.prescriptionMethod !== "camera") {
      if (this.formData.lensType == "Bifocal/Progressive") {
        if (addSection) {
          addSection.style.display = "block";
        }
        const brAddition = this.querySelector(".prescription-table-br-addition");
        if (brAddition) {
          brAddition.style.display = "";
        }
      } else {
        if (addSection) {
          addSection.style.display = "none";
        }
        const brAddition = this.querySelector(".prescription-table-br-addition");
        if (brAddition) {
          brAddition.style.display = "none";
        }
      }
    }

    // Handle upload section visibility and field hiding based on prescription method
    const uploadSection = this.querySelector(".prescription-pdf-upload-section");

    if (uploadSection) {
      if (this.formData.prescriptionMethod === "manual") {
        uploadSection.style.display = "none";
        // Show all prescription fields for manual entry
        if (prescriptionTable) {
          prescriptionTable.style.display = "";
        }
        if (prescriptionAdditionContainer) {
          prescriptionAdditionContainer.style.display = "";
        }
        if (checkboxContainer) {
          checkboxContainer.style.display = "";
        }
      } else if (
        this.formData.prescriptionMethod === "upload" ||
        this.formData.prescriptionMethod === "camera"
      ) {
        uploadSection.style.display = "block";
        // Hide prescription fields (SPH, CYL, AXI, ADD) when upload/camera method is selected
        if (prescriptionTable) {
          prescriptionTable.style.display = "none";
        }
        if (addSection) {
          addSection.style.display = "none";
        }
        const brAddition = this.querySelector(".prescription-table-br-addition");
        if (brAddition) {
          brAddition.style.display = "none";
        }
        // Hide "Same prescription" checkbox
        if (checkboxContainer) {
          checkboxContainer.style.display = "none";
        }
        // Keep PD section visible
        if (pdSection) {
          pdSection.style.display = "";
        }
        // Keep Prism section visible
        if (prismSection) {
          prismSection.style.display = "";
        }

        // Show the upload input for "upload", the camera input for "camera" -
        // unless a file has already been captured/uploaded, in which case both
        // stay hidden behind the shared success panel.
        const isCamera = this.formData.prescriptionMethod === "camera";
        const hasFile = !!(this.formData.pdfUrl && this.formData.pdfUrl.trim() !== "");
        const pdfUploadWrapper = this.querySelector(".pdf-upload-input-wrapper");
        const cameraUploadWrapper = this.querySelector(".camera-upload-input-wrapper");
        const uploadDifferentBtn = this.querySelector(".pdf-upload-different");
        if (pdfUploadWrapper) {
          pdfUploadWrapper.style.display = !isCamera && !hasFile ? "block" : "none";
        }
        if (cameraUploadWrapper) {
          cameraUploadWrapper.style.display = isCamera && !hasFile ? "flex" : "none";
        }
        if (uploadDifferentBtn) {
          uploadDifferentBtn.textContent = isCamera ? "Retake Photo" : "Upload a different file";
        }
      } else {
        uploadSection.style.display = "block";
        // Show all fields for other methods (saved, later)
        if (prescriptionTable) {
          prescriptionTable.style.display = "";
        }
        if (prescriptionAdditionContainer) {
          prescriptionAdditionContainer.style.display = "";
        }
        if (checkboxContainer) {
          checkboxContainer.style.display = "";
        }
      }
    }
  }
  renderMaterialView() {
    const currentLensType = this.formData?.lensType;
    this.querySelectorAll("rx-add-on").forEach((item) => {
      if (!item.dataset.includedLensType) {
        item.classList.remove("hidden");
        return;
      }

      if (!currentLensType) {
        item.classList.remove("hidden");
        return;
      }

      try {
        const parsedIncludedLensType = JSON.parse(item.dataset.includedLensType);
        if (Array.isArray(parsedIncludedLensType) && parsedIncludedLensType.includes(currentLensType)) {
          item.classList.remove("hidden");
        } else {
          item.classList.add("hidden");
        }
      } catch (e) {
        console.warn("Error parsing includedLensType:", e);
        item.classList.remove("hidden");
      }
    });
  }
  updateReviewSection() {
    // First, let's clear any existing content
    const lensDetails = this.querySelector("#lens-details");
    const lensPrescription = this.querySelector("#lens-prescription");
    if (!lensDetails) return;

    // Start with lens type
    const typePriceHtml = this._rxFormatMoney(0);
    const typeRow = `
      <div class="lens-spec-row">
        <span class="spec-label">Type</span>
        <span class="spec-value">${this.formData.lensType}</span>
        <span class="spec-price">${typePriceHtml}</span>
      </div>
    `;

    let specificationRows = "";

    if (this.formData.lensSpecification) {
      Object.entries(this.formData.lensSpecification).forEach(([key, data]) => {
        if (!data || !data.title || data.title === '-' || data.title === '') return;

        const displayLabel = key === 'Lens Material' ? 'Material' : key;
        let priceHtml = '';
        if (data.price && data.price.html) {
          priceHtml = data.price.html;
        } else if (data.price && data.price.value !== undefined) {
          const priceValue = parseFloat(data.price.value) || 0;
          priceHtml = this._rxFormatMoney(priceValue);
        } else {
          priceHtml = this._rxFormatMoney(0);
        }

        specificationRows += `
          <div class="lens-spec-row">
            <span class="spec-label">${displayLabel}</span>
            <span class="spec-value">${data.title}</span>
            <span class="spec-price">${priceHtml}</span>
          </div>
        `;
      });
    }

    // Add Prism row if prism is selected
    if (this.formData.prescription && this.formData.prescription.isAddPrism && this.formData.prescription.prism) {
      const prismPrice = 19.00;
      const prismPriceHtml = this._rxFormatMoney(prismPrice * 100);
      specificationRows += `
        <div class="lens-spec-row">
          <span class="spec-label">Prism</span>
          <span class="spec-value">Selected</span>
          <span class="spec-price">${prismPriceHtml}</span>
        </div>
      `;
    }
    let prescriptionTable = "";
    const prescriptionMethod = this.formData.prescriptionMethod;
    const isManualEntry = prescriptionMethod === "manual";

    if (isManualEntry && this.formData.prescription && (this.formData.prescription.right || this.formData.prescription.left)) {
      const right = this.formData.prescription.right || {};
      const left = this.formData.prescription.left || {};
      const ADD = this.formData.prescription.ADD || "";
      const PD = this.formData.prescription.PD || "";
      const PD_right = this.formData.prescription.PD_right || "";
      const PD_left = this.formData.prescription.PD_left || "";
      const isSplitPd = this.formData.prescription.isSplitPd;

      let pdRightValue = "";
      let pdLeftValue = "";
      if (isSplitPd) {
        pdRightValue = PD_right || "";
        pdLeftValue = PD_left || "";
      } else {
        pdRightValue = PD || "";
        pdLeftValue = PD || "";
      }

      prescriptionTable = `
        <div class="prescription-table">
          <div class="prescription-table-header">
            <div class="prescription-table-header-cell">
              <span></span>
            </div>
            <div class="prescription-table-header-cell">
              <span>SPH</span>
            </div>
            <div class="prescription-table-header-cell">
              <span>CYL</span>
            </div>
            <div class="prescription-table-header-cell">
              <span>AXI</span>
            </div>
            <div class="prescription-table-header-cell">
              <span>ADD</span>
            </div>
            <div class="prescription-table-header-cell">
              <span>PD</span>
            </div>
          </div>
          <div class="prescription-table-row">
            <div class="prescription-table-cell-label prescription-table-cell-label-right">Right(OD)</div>
            <div class="prescription-table-cell">
              <span>${right.SPH || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${right.CYL || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${right.AXI || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${ADD || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${pdRightValue}</span>
            </div>
          </div>
          <div class="prescription-table-row">
            <div class="prescription-table-cell-label prescription-table-cell-label-left">Left(OS)</div>
            <div class="prescription-table-cell">
              <span>${left.SPH || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${left.CYL || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${left.AXI || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${ADD || ""}</span>
            </div>
            <div class="prescription-table-cell">
              <span>${pdLeftValue}</span>
            </div>
          </div>
        </div>
      `;

      if (this.formData.prescription.isAddPrism && this.formData.prescription.prism) {
        const prism = this.formData.prescription.prism;
        let prismRows = "";

        const formatPrismValue = (value) => {
          if (!value || value === "n/a" || value === "") return value;
          const num = parseFloat(value);
          if (isNaN(num)) return value;
          if (num % 1 === 0) {
            return num.toString();
          }
          return num.toString().replace(/\.?0+$/, '');
        };

        if (prism.right.vertical !== "n/a" || prism.right.horizontal !== "n/a") {
          const prismParts = [];
          if (prism.right.vertical !== "n/a" && prism.right.vertical !== "" && prism.right.verticalBase !== "n/a" && prism.right.verticalBase !== "") {
            const baseLower = prism.right.verticalBase.toLowerCase();
            const formattedValue = formatPrismValue(prism.right.vertical);
            prismParts.push(`OD ΔV: ${formattedValue} ${baseLower}`);
          }
          if (prism.right.horizontal !== "n/a" && prism.right.horizontal !== "" && prism.right.horizontalBase !== "n/a" && prism.right.horizontalBase !== "") {
            const baseLower = prism.right.horizontalBase.toLowerCase();
            const formattedValue = formatPrismValue(prism.right.horizontal);
            prismParts.push(`OD ΔH: ${formattedValue} ${baseLower}`);
          }
          if (prismParts.length > 0) {
            prismRows += `
              <div class="prescription-table-row">
                <div class="prescription-table-cell-label">OD Δ</div>
                <div class="prescription-table-cell" style="grid-column: 2 / 7;">
                  <span>${prismParts.join(", ")}</span>
                </div>
              </div>
            `;
          }
        }

        if (prism.left.vertical !== "n/a" || prism.left.horizontal !== "n/a") {
          const prismParts = [];
          if (prism.left.vertical !== "n/a" && prism.left.vertical !== "" && prism.left.verticalBase !== "n/a" && prism.left.verticalBase !== "") {
            const baseLower = prism.left.verticalBase.toLowerCase();
            const formattedValue = formatPrismValue(prism.left.vertical);
            prismParts.push(`OS ΔV: ${formattedValue} ${baseLower}`);
          }
          if (prism.left.horizontal !== "n/a" && prism.left.horizontal !== "" && prism.left.horizontalBase !== "n/a" && prism.left.horizontalBase !== "") {
            const baseLower = prism.left.horizontalBase.toLowerCase();
            const formattedValue = formatPrismValue(prism.left.horizontal);
            prismParts.push(`OS ΔH: ${formattedValue} ${baseLower}`);
          }
          if (prismParts.length > 0) {
            prismRows += `
              <div class="prescription-table-row">
                <div class="prescription-table-cell-label">OS Δ</div>
                <div class="prescription-table-cell" style="grid-column: 2 / 7;">
                  <span>${prismParts.join(", ")}</span>
                </div>
              </div>
            `;
          }
        }

        if (prismRows) {
          prescriptionTable += prismRows;
        }
      }
    }

    // Update the content
    lensDetails.innerHTML = typeRow + specificationRows;

    if (lensPrescription) {
      lensPrescription.innerHTML = prescriptionTable;
    }

    const prescriptionTitle = this.querySelector("#prescription-title");
    if (prescriptionTitle) {
      if (prescriptionTable && prescriptionTable.trim() !== "") {
        prescriptionTitle.style.display = "block";
      } else {
        prescriptionTitle.style.display = "none";
      }
    }

    this.updateTotalPrice();
  }
  getCurrentSelectedVariant() {
    const variantPicker = document.querySelector("variant-picker");
    return variantPicker
      ? variantPicker.getSelectedVariant()
      : this.product.variants[0];
  }

  _resolveFrameHeroImageSrc(variant) {
    const p = this.product;
    const vFi = variant?.featured_image;
    if (vFi) {
      if (typeof vFi === "string") return vFi;
      if (vFi.src) return vFi.src;
    }
    const pf = p?.featured_image;
    if (typeof pf === "string") return pf;
    if (pf?.src) return pf.src;
    const img0 = p?.images?.[0];
    if (typeof img0 === "string") return img0;
    if (img0?.src) return img0;
    const attr = this.getAttribute("data-frame-fallback-image");
    if (attr) return attr;
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23e8e8e8' width='400' height='300'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-family='system-ui,sans-serif' font-size='14'%3EPhoto%3C/text%3E%3C/svg%3E";
  }

  /**
   * PDP themes expose `theme.Currency.formatMoney`; RX completion page often does not.
   * Prefer `data-money-format` on `rx-configurator` (set in liquid), then theme / Shopify globals.
   */
  _rxMoneyFormat() {
    try {
      if (typeof theme !== "undefined" && theme?.settings?.moneyFormat) {
        return theme.settings.moneyFormat;
      }
    } catch (_) {
      /* ignore */
    }
    const fromAttr = (this.dataset?.moneyFormat || "").trim();
    if (fromAttr) return fromAttr;
    if (typeof Shopify !== "undefined" && Shopify.currency?.money_format) {
      return Shopify.currency.money_format;
    }
    if (typeof Shopify !== "undefined" && Shopify.money_format) {
      return Shopify.money_format;
    }
    return "${{amount}}";
  }

  _rxFormatMoney(cents) {
    const fmt = this._rxMoneyFormat();
    try {
      if (typeof theme !== "undefined" && theme.Currency?.formatMoney) {
        return theme.Currency.formatMoney(cents, fmt);
      }
    } catch (_) {
      /* ignore */
    }
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents, fmt);
    }
    const n = (Number(cents) || 0) / 100;
    return `$${n.toFixed(2)}`;
  }

  updateToSelectedVariantData() {
    const productImage = document.querySelector(".solstice__product-new-image-viewer-main-image img");
    const configuratorImage = this.querySelector(".rx-configurator-product-image img");

    if (productImage && configuratorImage) {
      configuratorImage.src = productImage.getAttribute("src") || productImage.src;
    } else if (configuratorImage) {
      const variant = this.getCurrentSelectedVariant();
      const src = this._resolveFrameHeroImageSrc(variant);
      if (src) configuratorImage.src = src;
    }
    this.updateTotalPrice();
  }
  updateTotalPrice() {
    let total = 0;
    const selectedVariant = this.getCurrentSelectedVariant();
    total += selectedVariant.price;
    let hasTag = false;
    let discountAmount = 0;

    if (this.product && this.product.tags) {
      const discountTag = this.product.tags.find((tag) =>
        tag.startsWith("feed_import_discounted_")
      );
      if (discountTag) {
        const discountValue = discountTag.split("feed_import_discounted_")[1];
        discountAmount = parseFloat(discountValue) / 100;
        hasTag = true;
      }
    }

    if (hasTag) {
      total = total * (1 - discountAmount);
    }
    if (this.formData.lensSpecification) {
      Object.values(this.formData.lensSpecification).forEach((spec) => {
        if (spec.price && spec.price.value) {
          const priceValue = parseFloat(spec.price.value);
          total += priceValue; // Convert cents to dollars
        }
      });
    }

    if (this.formData.prescription && this.formData.prescription.isAddPrism && this.formData.prescription.prism) {
      total += 1900;
    }

    const totalElements = this.querySelectorAll(".total-price");
    const formattedTotal = this._rxFormatMoney(total);
    totalElements.forEach((totalElement) => {
      totalElement.textContent = formattedTotal;
    });
  }

  updateProgress(viewObject) {
    const progressFill = this.querySelector(".rx-progress-bar-fill");
    if (!progressFill) {
      return;
    }

    const currentStep = viewObject.step;

    const availableSteps = [];
    Object.values(this.viewsObject).forEach((view) => {
      if (!this.shouldSkipStep(view)) {
        availableSteps.push(view.step);
      }
    });

    availableSteps.sort((a, b) => a - b);

    const currentStepIndex = availableSteps.indexOf(currentStep);
    const totalAvailableSteps = availableSteps.length;

    let progressPercentage = 0;
    if (currentStepIndex >= 0 && totalAvailableSteps > 0) {
      if (currentStepIndex === 0) {
        progressPercentage = 5;
      } else if (currentStepIndex === totalAvailableSteps - 1) {
        progressPercentage = 100;
      } else {
        const baseProgress = 5;
        const remainingProgress = 95;
        const stepProgress = remainingProgress / (totalAvailableSteps - 1);
        progressPercentage = baseProgress + (currentStepIndex * stepProgress);
      }
    }

    progressFill.style.width = `${progressPercentage}%`;
  }

  updateContinueButton() {
    // Always enable the continue button
    // Errors will be shown when the button is clicked
    this.continueButton.disabled = false;

    // For prescription step, clear any previous errors when fields change
    if (this.currentStep === 3) {
      // Don't clear errors here - let them persist until continue is clicked
      // Errors will be shown/cleared in handleContinue when validation runs
    }
  }

  showPrescriptionError(message, errorFields = []) {
    // Only show error if we're on step 3 (prescription step)
    // This prevents errors from showing on other steps
    if (this.currentStep !== 3) {
      console.warn('⚠️ Attempted to show prescription error but not on step 3. Current step:', this.currentStep);
      return;
    }

    // Remove existing error
    this.clearPrescriptionError();

    // Create error element
    const errorEl = document.createElement("div");
    errorEl.className = "prescription-validation-error";
    errorEl.style.cssText = `
      display: block;
      width: 100%;
      background-color: #FEF2F2;
      border: 1px solid #DC2626;
      border-radius: 6px;
      padding: 12px 16px;
      margin: 0 0 16px 0;
      font-family: Inter, sans-serif;
      font-size: 13px;
      line-height: 1.5;
      color: #DC2626;
      font-weight: 500;
      box-sizing: border-box;
    `;
    errorEl.textContent = message;

    // Insert before continue button in the button container
    const buttonContainer = this.querySelector('.button-container');
    if (buttonContainer) {
      buttonContainer.insertBefore(errorEl, buttonContainer.firstChild);
    }

    // Highlight error fields with red border
    if (errorFields.length > 0) {
      errorFields.forEach(field => {
        if (field) {
          field.style.borderColor = '#DC2626';
          field.style.borderWidth = '2px';
          field.classList.add('prescription-field-error');
        }
      });
    }
  }

  clearPrescriptionError() {
    const errorEl = this.querySelector('.prescription-validation-error');
    if (errorEl) {
      errorEl.remove();
    }

    // Clear all field error styling
    const errorFields = this.querySelectorAll('.prescription-field-error');
    errorFields.forEach(field => {
      field.style.borderColor = '';
      field.style.borderWidth = '';
      field.classList.remove('prescription-field-error');
    });
  }

  showMaterialError(message) {
    // Only show error if we're on step 4 (material step)
    // This prevents errors from showing on other steps
    if (this.currentStep !== 4) {
      console.warn('⚠️ Attempted to show material error but not on step 4. Current step:', this.currentStep);
      return;
    }

    // Remove existing error
    this.clearMaterialError();

    // Create error element
    const errorEl = document.createElement("div");
    errorEl.className = "material-validation-error";
    errorEl.style.cssText = `
      display: block;
      width: 100%;
      background-color: #FEF2F2;
      border: 1px solid #DC2626;
      border-radius: 6px;
      padding: 12px 16px;
      margin: 0 0 16px 0;
      font-family: Inter, sans-serif;
      font-size: 13px;
      line-height: 1.5;
      color: #DC2626;
      font-weight: 500;
      box-sizing: border-box;
    `;
    errorEl.textContent = message;

    // Insert before continue button in the button container
    const buttonContainer = this.querySelector('.button-container');
    if (buttonContainer) {
      buttonContainer.insertBefore(errorEl, buttonContainer.firstChild);
    }
  }

  clearMaterialError() {
    const errorEl = this.querySelector('.material-validation-error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  showCoatingError(message) {
    // Only show error if we're on step 6 (coating step)
    // This prevents errors from showing on other steps
    if (this.currentStep !== 6) {
      console.warn('⚠️ Attempted to show coating error but not on step 6. Current step:', this.currentStep);
      return;
    }

    // Remove existing error
    this.clearCoatingError();

    // Create error element
    const errorEl = document.createElement("div");
    errorEl.className = "coating-validation-error";
    errorEl.style.cssText = `
      display: block;
      width: 100%;
      background-color: #FEF2F2;
      border: 1px solid #DC2626;
      border-radius: 6px;
      padding: 12px 16px;
      margin: 0 0 16px 0;
      font-family: Inter, sans-serif;
      font-size: 13px;
      line-height: 1.5;
      color: #DC2626;
      font-weight: 500;
      box-sizing: border-box;
    `;
    errorEl.textContent = message;

    // Insert before continue button in the button container
    const buttonContainer = this.querySelector('.button-container');
    if (buttonContainer) {
      buttonContainer.insertBefore(errorEl, buttonContainer.firstChild);
    }
  }

  clearCoatingError() {
    const errorEl = this.querySelector('.coating-validation-error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  getDropdownValue(el) {
    if (!el) return "";
    if (el.tagName === "SELECT") {
      return el.selectedOptions[0]?.value || "";
    }
    // For custom dropdown: look for the hidden input
    const hidden = el.querySelector('input[type="hidden"]');
    return hidden ? hidden.value : "";
  }

  validatePrescription(showErrors = false) {
    // Only show errors when explicitly requested (on Continue button click) AND we're on step 3
    // Clear previous errors when showing new ones
    if (showErrors && this.currentStep === 3) {
      this.clearPrescriptionError();
    }

    const prescriptionEntry = this.views.prescription;

    if (prescriptionEntry && prescriptionEntry.validatePrismRules) {
      // Only pass showErrors to prism validation if we're on step 3
      const prismValidation = prescriptionEntry.validatePrismRules(showErrors && this.currentStep === 3);
      if (!prismValidation.valid) {
        return false;
      }
    }

    // PD is always required, even with PDF upload
    // Check if split PD is enabled
    var splitPdCheckbox = this.views.prescription.querySelector("#split-pd-checkbox");
    var isSplitPd = splitPdCheckbox && splitPdCheckbox.checked;

    if (isSplitPd) {
      var pdRight = this.views.prescription.querySelector("#pd-right");
      var pdLeft = this.views.prescription.querySelector("#pd-left");
      var pdRightValue = pdRight ? this.getDropdownValue(pdRight) : "";
      var pdLeftValue = pdLeft ? this.getDropdownValue(pdLeft) : "";
      if (!pdRightValue || !pdLeftValue || pdRightValue === "" || pdLeftValue === "") {
        if (showErrors && this.currentStep === 3) {
          const errorFields = [];
          if (!pdRightValue || pdRightValue === "") errorFields.push(pdRight);
          if (!pdLeftValue || pdLeftValue === "") errorFields.push(pdLeft);
          this.showPrescriptionError("Please select PD (Pupillary Distance) values for both eyes.", errorFields);
        }
        return false;
      }
      // Range is age-based, rendered by Liquid as data-pd-min/data-pd-max
      // (kids' frames get a lower range) — fall back to the adult range.
      var splitPdMin = parseFloat(pdRight?.dataset.pdMin) || 27.0;
      var splitPdMax = parseFloat(pdRight?.dataset.pdMax) || 37.0;
      var pdRightNum = parseFloat(pdRightValue);
      var pdLeftNum = parseFloat(pdLeftValue);
      if (isNaN(pdRightNum) || isNaN(pdLeftNum) || pdRightNum < splitPdMin || pdRightNum > splitPdMax || pdLeftNum < splitPdMin || pdLeftNum > splitPdMax) {
        if (showErrors && this.currentStep === 3) {
          const errorFields = [];
          if (isNaN(pdRightNum) || pdRightNum < splitPdMin || pdRightNum > splitPdMax) errorFields.push(pdRight);
          if (isNaN(pdLeftNum) || pdLeftNum < splitPdMin || pdLeftNum > splitPdMax) errorFields.push(pdLeft);
          this.showPrescriptionError(`Split PD values must be between ${splitPdMin} and ${splitPdMax} for each eye.`, errorFields);
        }
        return false;
      }
    } else {
      var pd = this.views.prescription.querySelector(
        ".prescription-select-rest[name='PD']"
      );
      var pdValue = this.getDropdownValue(pd);
      if (!pdValue || pdValue === "") {
        if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select a PD (Pupillary Distance) value.", [pd]);
        return false;
      }
      // Range is age-based, rendered by Liquid as data-pd-min/data-pd-max
      // (kids' frames get a lower range) — fall back to the adult range.
      var pdMin = parseFloat(pd?.dataset.pdMin) || 54.0;
      var pdMax = parseFloat(pd?.dataset.pdMax) || 74.0;
      var pdNum = parseFloat(pdValue);
      if (isNaN(pdNum) || pdNum < pdMin || pdNum > pdMax) {
        if (showErrors && this.currentStep === 3) this.showPrescriptionError(`PD value must be between ${pdMin} and ${pdMax}.`, [pd]);
        return false;
      }
    }

    if (this.formData.prescriptionMethod === "upload" || this.formData.prescriptionMethod === "camera") {
      if (this.formData.pdfUrl && this.formData.pdfUrl.trim() !== "") {
        return true;
      }
      if (showErrors && this.currentStep === 3) {
        const uploadContainer = this.views.prescription.querySelector(".prescription-pdf-upload-container");
        this.showPrescriptionError(
          this.formData.prescriptionMethod === "camera"
            ? "Please take a picture of your prescription before continuing."
            : "Please upload your prescription (PDF or image) before continuing.",
          uploadContainer ? [uploadContainer] : []
        );
      }
      return false;
    }

    if (this.formData.pdfUrl && this.formData.pdfUrl.trim() !== "") {
      return true;
    }

    // Read checkbox state directly to ensure we have the current value
    const samePrescriptionCheckbox = this.views.prescription.querySelector("#same-prescription-checkbox");
    const sameForBothEyes = samePrescriptionCheckbox ? samePrescriptionCheckbox.checked : (this.formData.prescription?.isSamePrescription || false);

    var cylLeft = this.views.prescription.querySelector(
      ".prescription-select-left[name='CYL']"
    );
    var cylRight = this.views.prescription.querySelector(
      ".prescription-select-right[name='CYL']"
    );
    var axisLeft = this.views.prescription.querySelector(
      ".prescription-select-left[name='AXI']"
    );
    var axisRight = this.views.prescription.querySelector(
      ".prescription-select-right[name='AXI']"
    );
    var sphLeft = this.views.prescription.querySelector(
      ".prescription-select-left[name='SPH']"
    );
    var sphRight = this.views.prescription.querySelector(
      ".prescription-select-right[name='SPH']"
    );
    var add = this.views.prescription.querySelector(
      ".prescription-select-rest[name='ADD']"
    );
    if (sameForBothEyes) {
      cylLeft = cylRight;
      axisLeft = axisRight;
      sphLeft = sphRight;
    }
    switch (this.formData.lensType) {
      case "Single Vision":
      case "Progressive":
      case "Bifocal":
      case "Bifocal/Progressive":
        var noSphereSelected =
          this.getDropdownValue(sphLeft) === "" ||
          this.getDropdownValue(sphRight) === "";
        if (noSphereSelected) {
          if (showErrors && this.currentStep === 3) {
            const errorFields = [];
            if (this.getDropdownValue(sphLeft) === "") errorFields.push(sphLeft);
            if (this.getDropdownValue(sphRight) === "") errorFields.push(sphRight);
            this.showPrescriptionError("Please select Sphere (SPH) values for both eyes.", errorFields);
          }
          return false;
        }

        var cylRightValue = this.getDropdownValue(cylRight);
        var cylLeftValue = this.getDropdownValue(cylLeft);
        var axisRightValue = this.getDropdownValue(axisRight);
        var axisLeftValue = this.getDropdownValue(axisLeft);

        var cylRightSelectedAndNonZero =
          cylRightValue != "0" && cylRightValue != "";
        var axisRightNotSelected = axisRightValue === "";
        var cylLeftSelectedAndNonZero =
          cylLeftValue != "0" && cylLeftValue != "";
        var axisLeftNotSelected = axisLeftValue === "";

        // Rule: If CYL is non-zero, axis must be selected
        if (cylRightSelectedAndNonZero && axisRightNotSelected) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select an Axis (AXI) value for the right eye when Cylinder (CYL) is selected.", [axisRight]);
          return false;
        }
        if (cylLeftSelectedAndNonZero && axisLeftNotSelected) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select an Axis (AXI) value for the left eye when Cylinder (CYL) is selected.", [axisLeft]);
          return false;
        }

        // Rule: If axis is selected, CYL must be non-zero (not 0 or empty)
        // This means: if axis is selected AND (CYL is 0 or empty), it's invalid
        if (!axisRightNotSelected && (cylRightValue === "0" || cylRightValue === "")) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select a Cylinder (CYL) value for the right eye when Axis (AXI) is selected.", [cylRight]);
          return false;
        }
        if (!axisLeftNotSelected && (cylLeftValue === "0" || cylLeftValue === "")) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select a Cylinder (CYL) value for the left eye when Axis (AXI) is selected.", [cylLeft]);
          return false;
        }

        if (cylRightSelectedAndNonZero && cylLeftSelectedAndNonZero) {
          var cylRightNum = parseFloat(cylRightValue);
          var cylLeftNum = parseFloat(cylLeftValue);
          var cylRightSign = cylRightNum < 0 ? '-' : '+';
          var cylLeftSign = cylLeftNum < 0 ? '-' : '+';
          if (cylRightSign !== cylLeftSign) {
            if (showErrors && this.currentStep === 3) this.showPrescriptionError("Cylinder (CYL) signs must match for both eyes (both positive or both negative).", [cylRight, cylLeft]);
            return false;
          }
        }

        var addNotSelected = this.getDropdownValue(add) === "";
        if (
          (this.formData.lensType == "Progressive" ||
            this.formData.lensType == "Bifocal" ||
            this.formData.lensType == "Bifocal/Progressive") &&
          addNotSelected
        ) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select an ADD value for Progressive or Bifocal lenses.", [add]);
          return false;
        }

        return true;
      case "Non-Prescription":
        return true;
      case "Specialty Lenses":
        var cylRightValue = this.getDropdownValue(cylRight);
        var cylLeftValue = this.getDropdownValue(cylLeft);
        var axisRightValue = this.getDropdownValue(axisRight);
        var axisLeftValue = this.getDropdownValue(axisLeft);

        var cylRightSelectedAndNonZero =
          cylRightValue != "0" && cylRightValue != "";
        var axisRightNotSelected = axisRightValue === "";
        var cylLeftSelectedAndNonZero =
          cylLeftValue != "0" && cylLeftValue != "";
        var axisLeftNotSelected = axisLeftValue === "";

        // Rule: If CYL is non-zero, axis must be selected
        if (cylRightSelectedAndNonZero && axisRightNotSelected) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select an Axis (AXI) value for the right eye when Cylinder (CYL) is selected.", [axisRight]);
          return false;
        }
        if (cylLeftSelectedAndNonZero && axisLeftNotSelected) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select an Axis (AXI) value for the left eye when Cylinder (CYL) is selected.", [axisLeft]);
          return false;
        }

        // Rule: If axis is selected, CYL must be non-zero (not 0 or empty)
        // This means: if axis is selected AND (CYL is 0 or empty), it's invalid
        if (!axisRightNotSelected && (cylRightValue === "0" || cylRightValue === "")) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select a Cylinder (CYL) value for the right eye when Axis (AXI) is selected.", [cylRight]);
          return false;
        }
        if (!axisLeftNotSelected && (cylLeftValue === "0" || cylLeftValue === "")) {
          if (showErrors && this.currentStep === 3) this.showPrescriptionError("Please select a Cylinder (CYL) value for the left eye when Axis (AXI) is selected.", [cylLeft]);
          return false;
        }

        if (cylRightSelectedAndNonZero && cylLeftSelectedAndNonZero) {
          var cylRightNum = parseFloat(cylRightValue);
          var cylLeftNum = parseFloat(cylLeftValue);
          var cylRightSign = cylRightNum < 0 ? '-' : '+';
          var cylLeftSign = cylLeftNum < 0 ? '-' : '+';
          if (cylRightSign !== cylLeftSign) {
            if (showErrors && this.currentStep === 3) this.showPrescriptionError("Cylinder (CYL) signs must match for both eyes (both positive or both negative).", [cylRight, cylLeft]);
            return false;
          }
        }

        return true;
      default:
        return false;
    }
  }

  validateMaterial() {
    let canPass = true;
    const materialView = this.querySelector("#lensMaterialView");
    if (!materialView) return false;

    materialView.querySelectorAll("rx-add-on").forEach((item) => {
      if (item.classList.contains("hidden") || item.closest(".hidden")) return;
      item.handleDynamicPrice();
      canPass = canPass && item.validateAddOn();
      const input = item.querySelector("input");
      const formData = item.getSelectedAddOnFormData();
      if (formData) {
        this.formData.lensSpecification = {
          ...this.formData.lensSpecification,
          ...formData,
        };
        this.updateFooterSelections();
      }
    });
    return canPass;
  }
}
class LensTypeSelection {
  constructor() {
    this.options = document.querySelectorAll(".prescription-option[data-type]");
    this.init();
  }

  init() {
    this.options.forEach((option) => {
      option.addEventListener("click", () => {
        // Remove selected class from all options
        this.options.forEach((opt) => opt.classList.remove("selected"));
        // Add selected class to clicked option
        option.classList.add("selected");

        // Dispatch custom event with selected type
        const event = new CustomEvent("lensTypeSelected", {
          detail: { type: option.dataset.type },
          bubbles: true, // Enable bubbling
          composed: true, // Allow crossing shadow DOM boundaries
        });
        option.dispatchEvent(event); // Dispatch from the option element
      });
    });
  }
}
class LensMaterialSelector extends HTMLElement {
  constructor() {
    super();

    this.isMandatory = this.dataset.mandatory == "true";
    this.includedLensType = this.dataset.includedLensType
      ? JSON.parse(this.dataset.includedLensType)
      : [];
    this.selectedAddon = null;
    this.configurator = document.querySelector("rx-configurator");
    this.cardTitle = this.querySelector(".lens-material-title");
    this.cardPrice = this.querySelector(".lens-material-price");
    this.cardDescription = this.querySelector(
      ".lens-material-title-wrapper .tooltip"
    );
    if (this.dataset.isColor == "true") {
      this.currentColor = this.querySelector(".rx-selected_color");
      this.allColors = this.querySelectorAll(".lens-material-color-option");
      this.allColors.forEach((color) => {
        color.addEventListener("click", this.handleColorChange.bind(this));
      });
    } else {
      const checkedInput = this.querySelector("input[type='radio']:checked");
      console.log('🔧 LensMaterialSelector constructor - checkedInput:', checkedInput, 'isNavigatingBack:', this.configurator.isNavigatingBack);
      if (checkedInput && !this.configurator.isNavigatingBack) {
        console.log('🔧 Unchecking default input:', checkedInput.name, checkedInput.value);
        checkedInput.checked = false;

        // Clear from formData if it was synced
        const inputName = checkedInput.name;
        if (this.configurator.formData.lensSpecification && this.configurator.formData.lensSpecification[inputName]) {
          console.log('🔧 Clearing formData for:', inputName);
          delete this.configurator.formData.lensSpecification[inputName];
          this.configurator.updateContinueButton();
        }
      }

      this.querySelectorAll(".lens-material-radio-option").forEach(
        (inputRow) => {
          inputRow.addEventListener(
            "change",
            this.handleInputChange.bind(this)
          );
        }
      );
    }
  }
  validateAddOn() {
    console.log('✅ validateAddOn called - mandatory:', this.isMandatory, 'selectedAddon:', this.selectedAddon);
    if (!this.isMandatory) return true;

    // If includedLensType has values and current type is not included, skip validation
    const includedInSelectedType = this.includedLensType.includes(
      this.configurator.formData.lensType
    );
    console.log('✅ includedLensType:', this.includedLensType, 'current lensType:', this.configurator.formData.lensType, 'included:', includedInSelectedType);

    // Only skip if there ARE specific types defined AND current type is NOT in the list
    if (this.includedLensType.length > 0 && !includedInSelectedType) {
      console.log('✅ Skipping validation - not applicable to this lens type');
      return true;
    }

    if (!this.selectedAddon) {
      console.log('❌ No selectedAddon');
      return false;
    }

    // Check if the selected addon is "Clear" - Clear doesn't need color swatch selection
    const input = this.selectedAddon.querySelector("input");
    const selectedAddonTitle = input?.value || input?.dataset?.addonTitle || '';
    const isClearLens = selectedAddonTitle && selectedAddonTitle.trim().toLowerCase() === 'clear';

    console.log('🎨 Selected addon title:', selectedAddonTitle, 'isClearLens:', isClearLens);

    // If it's Clear lens, skip color picker validation
    if (isClearLens) {
      console.log('✅ Clear lens selected - skipping color swatch validation');
      return true;
    }

    const colorPicker = this.selectedAddon
      .closest(".lens-material-radio-option-container")
      ?.querySelector("color-picker-rx");

    console.log('🎨 ColorPicker found:', !!colorPicker, 'selectedColorButton:', colorPicker?.selectedColorButton);

    if (colorPicker) {
      // If no color is selected, try to auto-select one
      let selectedColorButton = colorPicker.selectedColorButton ||
        colorPicker.querySelector(".rx-selected_color");

      // If still no color selected, auto-select the first available (or single) color
      if (!selectedColorButton) {
        const colorOptions = colorPicker.querySelectorAll(".lens-material-color-option");
        if (colorOptions.length > 0) {
          selectedColorButton = colorOptions[0];
          // Mark it as selected
          colorOptions.forEach((btn) => btn.classList.remove("rx-selected_color"));
          selectedColorButton.classList.add("rx-selected_color");
          colorPicker.selectedColorButton = selectedColorButton;

          // Update header title
          const headerTitle = colorPicker.querySelector(
            ".lens-material-color-options-wrapper-header-title"
          );
          if (headerTitle && selectedColorButton.dataset.label) {
            headerTitle.textContent = selectedColorButton.dataset.label;
          }

          // Update formData immediately
          if (this.configurator) {
            const formData = this.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                this.configurator.formData.lensSpecification[key] = formData[key];
              });
              this.configurator.updateFooterSelections();
              this.configurator.updateTotalPrice();
            }
          }
        }
      }

      const hasSelectedColor = selectedColorButton &&
        selectedColorButton.classList.contains('rx-selected_color');
      console.log('🎨 Has selected color:', hasSelectedColor);

      if (!hasSelectedColor) {
        console.log('❌ ColorPicker exists but no color selected');
        return false;
      }
    }

    console.log('✅ Validation passed');
    return true;
  }
  handleColorChange(event) {
    if (this.currentColor == event.currentTarget) return;
    this.currentColor.classList.remove("rx-selected_color");
    this.currentColor = event.currentTarget;
    this.currentColor.classList.add("rx-selected_color");
    this.querySelector(
      ".lens-material-color-info-current-color-label"
    ).textContent = event.currentTarget.dataset.label;
    const customEvent = new CustomEvent("lensColorUpdated", {
      bubbles: true,
      composed: true,
      detail: {
        Color: {
          title: event.currentTarget.dataset.label,
          price: {
            value: 0,
            html: "$0.00",
          },
        },
      },
    });
    this.dispatchEvent(customEvent);
  }
  handleDynamicPrice() {
    const allDynamicPricesInputs = this.configurator.querySelectorAll(
      "input[data-dynamic-price]"
    );
    // reset to default price
    this.configurator
      .querySelectorAll("input[data-addon-price]")
      .forEach((input) => {
        input.dataset.addonPrice = input.dataset.addonDefaultPrice;
        input.dataset.addonPriceHtml = input.dataset.addonDefaultHtml;
        if (input.dataset.addonDefaultPrice == 0) return;

        const priceElement = input
          .closest(".lens-material-radio-option")
          ?.querySelector(".lens-material-radio-price");
        if (priceElement) {
          priceElement.textContent = input.dataset.addonDefaultHtml;
        }
      });
    // update to dynamic price when needed
    allDynamicPricesInputs.forEach((dynamicPriceInput) => {
      const dynamicPrice = JSON.parse(dynamicPriceInput.dataset.dynamicPrice);
      const dynamicPriceLabels = JSON.parse(
        dynamicPriceInput.dataset.dynamicPriceLabels
      );
      dynamicPriceLabels.forEach((label, index) => {
        const correspondingLabelIsSelected = this.configurator.querySelector(
          `input[value="${label}"]:checked`
        );
        if (!correspondingLabelIsSelected) return;
        const correspondingPrice = dynamicPrice[index];
        const priceValue = parseFloat(correspondingPrice.item.amount) * 100;
        dynamicPriceInput.dataset.addonPrice = priceValue;
        dynamicPriceInput.dataset.addonPriceHtml = this.configurator._rxFormatMoney(priceValue);
        dynamicPriceInput
          .closest(".lens-material-radio-option")
          .querySelector(".lens-material-radio-price").textContent =
          this.configurator._rxFormatMoney(priceValue);
      });
    });
    this.configurator.updateTotalPrice();
  }
  handleInputChange(event) {
    event.preventDefault && event.preventDefault();
    if (event.currentTarget.classList.contains("disabled")) return;
    const correspondingInput = event.currentTarget.querySelector("input");
    this.handleDynamicPrice(correspondingInput);

    this.querySelectorAll("color-picker-rx").forEach((picker) => {
      picker.hide();
    });

    if (this.selectedAddon === event.currentTarget) {
      if (this.isMandatory) return;
      this.selectedAddon = null;
      correspondingInput.checked = false;
      correspondingInput.dispatchEvent(new Event("change"));
    } else {
      correspondingInput.checked = true;
      correspondingInput.dispatchEvent(new Event("change"));
      this.selectedAddon = event.currentTarget;

      const colorPicker = event.currentTarget
        .closest(".lens-material-radio-option-container")
        .querySelector("color-picker-rx");

      if (colorPicker) {
        colorPicker.open();

        // After opening, ensure formData is updated with the selected color
        // This is especially important for single-swatch options that get auto-selected
        setTimeout(() => {
          const formData = this.getSelectedAddOnFormData();
          if (formData && this.configurator) {
            Object.keys(formData).forEach((key) => {
              this.configurator.formData.lensSpecification[key] = formData[key];
            });
            this.configurator.updateFooterSelections();
            this.configurator.updateTotalPrice();
            this.configurator.updateContinueButton();
          }
        }, 50);
      }
    }
    this.handleAddOnsThatDontWorkTogether();

    this.dispatchSelectionEvent();
  }
  handleAddOnsThatDontWorkTogether() {
    const highIndex = this.configurator.querySelector(
      'input[value="1.74 High-Index"]'
    );
    const transitionExtra = this.configurator.querySelector(
      'input[value^="Transition extra"]'
    );
    const bifocal = this.configurator.querySelector('input[value="Bifocal"]');

    // TODO: MAKE BETTER LOGIC FOR THIS
    if (highIndex.checked) {
      transitionExtra.disabled = true;
      transitionExtra.parentElement.classList.add("disabled");
      bifocal.disabled = true;
      bifocal.parentElement.classList.add("disabled");
    } else {
      if (transitionExtra.checked) {
        highIndex.disabled = true;
        highIndex.parentElement.classList.add("disabled");
      } else {
        highIndex.disabled = false;
        highIndex.parentElement.classList.remove("disabled");
        transitionExtra.disabled = false;
        transitionExtra.parentElement.classList.remove("disabled");
      }
      if (bifocal.checked) {
        highIndex.disabled = true;
        highIndex.parentElement.classList.add("disabled");
      } else {
        bifocal.disabled = false;
        bifocal.parentElement.classList.remove("disabled");
      }
    }
  }

  dispatchSelectionEvent() {
    const formData = this.getSelectedAddOnFormData();
    if (!formData) return;

    const event = new CustomEvent("lensMaterialSelected", {
      bubbles: true,
      composed: true,
      detail: formData,
    });
    this.dispatchEvent(event);

    if (this.configurator) {
      setTimeout(() => {
        this.configurator.updateFooterSelections();
        this.configurator.updateTotalPrice();
      }, 10);
    }
  }
  getSelectedAddOnFormData() {
    if (this.classList.contains("hidden")) return null;

    if (!this.selectedAddon) {
      const input = this.querySelector(
        `input[name="${this.dataset.groupTitle}"]`
      );
      return {
        [input.name]: {
          title: "N/A",
          price: {
            value: 0,
            html: this.configurator._rxFormatMoney(0),
          },
        },
      };
    }
    const input = this.selectedAddon.querySelector("input");
    const colorPicker = this.selectedAddon
      .closest(".lens-material-radio-option-container")
      .querySelector("color-picker-rx");
    if (colorPicker) {
      // Get selected color button - check both selectedColorButton property and fallback to rx-selected_color class
      let selectedColorButton = colorPicker.selectedColorButton ||
        colorPicker.querySelector(".rx-selected_color");

      // If still no color selected, auto-select the first available color
      if (!selectedColorButton) {
        const colorOptions = colorPicker.querySelectorAll(".lens-material-color-option");
        if (colorOptions.length > 0) {
          selectedColorButton = colorOptions[0];
          // Mark it as selected
          colorOptions.forEach((btn) => btn.classList.remove("rx-selected_color"));
          selectedColorButton.classList.add("rx-selected_color");
          colorPicker.selectedColorButton = selectedColorButton;

          // Update header title
          const headerTitle = colorPicker.querySelector(
            ".lens-material-color-options-wrapper-header-title"
          );
          if (headerTitle && selectedColorButton.dataset.label) {
            headerTitle.textContent = selectedColorButton.dataset.label;
          }
        }
      }

      if (selectedColorButton && selectedColorButton.dataset.label) {
        const colorLabel = selectedColorButton.dataset.label;
        let title =
          colorLabel === input.value ? input.value : `${input.value} - ${colorLabel}`;
        if (colorPicker.tintSlider)
          title = `${title} - ${colorPicker.tintSlider.value}% darkness`;
        const priceValue = parseFloat(input.dataset.addonPrice) || 0;
        const priceHtml = input.dataset.addonPriceHtml || this.configurator._rxFormatMoney(priceValue);
        return {
          [input.name]: {
            title: title,
            price: {
              value: priceValue,
              html: priceHtml,
            },
          },
        };
      } else {
        // Fallback: return data without color label (shouldn't happen, but just in case)
        const priceValue = parseFloat(input.dataset.addonPrice) || 0;
        const priceHtml = input.dataset.addonPriceHtml || this.configurator._rxFormatMoney(priceValue);
        return {
          [input.name]: {
            title: input.value,
            price: {
              value: priceValue,
              html: priceHtml,
            },
          },
        };
      }
    }

    const priceValue = parseFloat(input.dataset.addonPrice) || 0;
    const priceHtml =
      input.dataset.addonPriceHtml ||
      (this.configurator && this.configurator._rxFormatMoney
        ? this.configurator._rxFormatMoney(priceValue)
        : typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function"
          ? Shopify.formatMoney(priceValue, "${{amount}}")
          : `$${((Number(priceValue) || 0) / 100).toFixed(2)}`);
    return {
      [input.name]: {
        title: input.value,
        price: {
          value: priceValue,
          html: priceHtml,
        },
      },
    };
  }
}

class PrescriptionEntry extends HTMLElement {
  constructor() {
    super();
    // Initialize DOM elements
    this.leftEyeRow = this.querySelector(
      ".prescription-table-row:last-of-type"
    );
    this.rightEyeLabel = this.querySelector(
      ".prescription-table-cell-label-right"
    );

    // Initialize select elements
    this.rightEyeSelects = Array.from(
      this.querySelectorAll(".prescription-select-right")
    );
    this.leftEyeSelects = Array.from(
      this.querySelectorAll(".prescription-select-left")
    );

    this.restSelects = Array.from(
      this.querySelectorAll(".prescription-select-rest")
    );

    // Initialize merge checkbox
    this.mergeCheckbox = this.querySelector("#same-prescription-checkbox");

    // Initialize split PD checkbox and fields
    this.splitPdCheckbox = this.querySelector("#split-pd-checkbox");
    this.pdSingle = this.querySelector("#pd-single");
    this.pdRight = this.querySelector("#pd-right");
    this.pdLeft = this.querySelector("#pd-left");
    this.pdSplitRows = this.querySelectorAll(".prescription-table-row-pd-split");

    // Initialize Add Prism checkbox and fields
    this.addPrismCheckbox = this.querySelector("#add-prism-checkbox");
    this.prismFields = this.querySelector(".prescription-prism-fields");
    this.prismVerticalRight = this.querySelector("#prism-vertical-right");
    this.prismVerticalBaseRight = this.querySelector("#prism-vertical-base-right");
    this.prismHorizontalRight = this.querySelector("#prism-horizontal-right");
    this.prismHorizontalBaseRight = this.querySelector("#prism-horizontal-base-right");
    this.prismVerticalLeft = this.querySelector("#prism-vertical-left");
    this.prismVerticalBaseLeft = this.querySelector("#prism-vertical-base-left");
    this.prismHorizontalLeft = this.querySelector("#prism-horizontal-left");
    this.prismHorizontalBaseLeft = this.querySelector("#prism-horizontal-base-left");
    new LensTypeSelection();
    // new LensMaterialSelector();
    // Single document click listener for all dropdowns
    document.addEventListener("click", (e) => {
      document
        .querySelectorAll(".custom-dropdown.open")
        .forEach((openDropdown) => {
          if (!openDropdown.contains(e.target)) {
            openDropdown.classList.remove("open");
          }
        });
    });

    this.querySelectorAll(".custom-dropdown").forEach((dropdown) => {
      const selected = dropdown.querySelector(".custom-dropdown-selected");
      const options = dropdown.querySelectorAll(".custom-dropdown-option");
      const hiddenInput = dropdown.querySelector('input[type="hidden"]');

      // Check if required elements exist
      if (!selected) {
        console.warn(
          "Custom dropdown missing .custom-dropdown-selected element:",
          dropdown
        );
        return;
      }

      selected.onclick = (e) => {
        e.stopPropagation();
        dropdown.classList.toggle("open");
      };

      options.forEach((option) => {
        option.onclick = () => {
          selected.textContent = option.textContent;

          // Only update hidden input if it exists
          if (hiddenInput) {
            hiddenInput.value = option.getAttribute("data-value");
          }

          dropdown.classList.remove("open");
          dropdown.dispatchEvent(new Event("change", { bubbles: true }));
        };
      });
    });
    this.init();
  }

  init() {
    if (this.mergeCheckbox) {
      this.mergeCheckbox.addEventListener("change", (e) => {
        this.merged = e.target.checked;
        this.handlePrescriptionMerge(e.target.checked);
        // Trigger validation update after merge state changes
        this.handlePrescriptionUpdate();
      });
    }

    // Handle split PD checkbox
    if (this.splitPdCheckbox) {
      this.splitPdCheckbox.addEventListener("change", (e) => {
        this.handleSplitPdToggle(e.target.checked);
        // Trigger validation when checkbox is toggled (but don't show errors yet)
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          // Use setTimeout to ensure DOM updates complete first
          setTimeout(() => {
            rxConfigurator.validatePrescription(false);
          }, 100);
        }
      });
    }

    // Handle Add Prism checkbox
    if (this.addPrismCheckbox) {
      this.addPrismCheckbox.addEventListener("change", (e) => {
        this.handleAddPrismToggle(e.target.checked);
        // Trigger validation when checkbox is toggled (but don't show errors yet)
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          // Use setTimeout to ensure DOM updates complete first
          setTimeout(() => {
            rxConfigurator.validatePrescription(false);
          }, 100);
        }
      });
    }

    // Handle prism field changes to enable/disable base direction fields
    this.initPrismFieldHandlers();

    // Add change listeners to all selects
    [
      ...this.rightEyeSelects,
      ...this.leftEyeSelects,
      ...this.restSelects,
    ].forEach((select) => {
      select.addEventListener("change", () => {
        this.handlePrescriptionUpdate();
        // Trigger validation on field change (but don't show errors yet)
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          rxConfigurator.validatePrescription(false);
        }
      });

      // Also trigger validation on blur (when leaving the field, but don't show errors)
      select.addEventListener("blur", () => {
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          rxConfigurator.validatePrescription(false);
        }
      });
    });

    // Add change listeners to split PD fields
    if (this.pdRight) {
      this.pdRight.addEventListener("change", () => {
        this.handlePrescriptionUpdate();
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          rxConfigurator.validatePrescription(false);
        }
      });
      this.pdRight.addEventListener("blur", () => {
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          rxConfigurator.validatePrescription(false);
        }
      });
    }
    if (this.pdLeft) {
      this.pdLeft.addEventListener("change", () => {
        this.handlePrescriptionUpdate();
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          rxConfigurator.validatePrescription(false);
        }
      });
      this.pdLeft.addEventListener("blur", () => {
        const rxConfigurator = this.closest('rx-configurator');
        if (rxConfigurator) {
          rxConfigurator.validatePrescription(false);
        }
      });
    }

    // Add change listeners to prism fields
    if (this.prismVerticalRight) {
      this.prismVerticalRight.addEventListener("change", () => {
        this.handlePrismDeltaChange(this.prismVerticalRight, this.prismVerticalBaseRight);
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismHorizontalRight) {
      this.prismHorizontalRight.addEventListener("change", () => {
        this.handlePrismDeltaChange(this.prismHorizontalRight, this.prismHorizontalBaseRight);
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismVerticalLeft) {
      this.prismVerticalLeft.addEventListener("change", () => {
        this.handlePrismDeltaChange(this.prismVerticalLeft, this.prismVerticalBaseLeft);
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismHorizontalLeft) {
      this.prismHorizontalLeft.addEventListener("change", () => {
        this.handlePrismDeltaChange(this.prismHorizontalLeft, this.prismHorizontalBaseLeft);
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismVerticalBaseRight) {
      this.prismVerticalBaseRight.addEventListener("change", () => {
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismHorizontalBaseRight) {
      this.prismHorizontalBaseRight.addEventListener("change", () => {
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismVerticalBaseLeft) {
      this.prismVerticalBaseLeft.addEventListener("change", () => {
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }
    if (this.prismHorizontalBaseLeft) {
      this.prismHorizontalBaseLeft.addEventListener("change", () => {
        // Update validation in real-time when values change (but don't show errors yet)
        if (this.addPrismCheckbox && this.addPrismCheckbox.checked) {
          setTimeout(() => this.validatePrismRules(false), 100);
        }
        this.handlePrescriptionUpdate();
      });
    }

    // Open a popup with the pd info
    this.querySelector(".prescription-table-row-pd-info").addEventListener(
      "click",
      () => {
        this.openPopupForPDInfo();
      }
    );
  }

  handleSplitPdToggle(isSplit) {
    const pdHint = this.querySelector(".pd-info-hint")
    const measurePD = this.querySelector(".prescription-pd-measure-wrap")
    if (isSplit) {
      measurePD.style.display = "none";
      pdHint.innerText = pdHint.getAttribute("data-split")

      // Hide single PD field, show split PD fields
      if (this.pdSingle) {
        this.pdSingle.closest(".prescription-table-row").style.display = "none";
        this.pdSingle.removeAttribute("required");
      }
      this.pdSplitRows.forEach((row) => {
        row.style.display = "grid";
      });
      if (this.pdRight) this.pdRight.setAttribute("required", "");
      if (this.pdLeft) this.pdLeft.setAttribute("required", "");
    } else {
      // Show single PD field, hide split PD fields
      measurePD.style.display = "block";
      pdHint.innerText = pdHint.getAttribute("data-single")
      if (this.pdSingle) {
        this.pdSingle.closest(".prescription-table-row").style.display = "grid";
        this.pdSingle.setAttribute("required", "");
      }
      this.pdSplitRows.forEach((row) => {
        row.style.display = "none";
      });
      if (this.pdRight) {
        this.pdRight.removeAttribute("required");
        this.pdRight.value = "";
      }
      if (this.pdLeft) {
        this.pdLeft.removeAttribute("required");
        this.pdLeft.value = "";
      }
    }
    this.handlePrescriptionUpdate();
  }

  handleAddPrismToggle(isEnabled) {
    if (this.prismFields) {
      this.prismFields.style.display = isEnabled ? "block" : "none";
    }
    if (!isEnabled) {
      // Reset all prism fields when disabled
      if (this.prismVerticalRight) this.prismVerticalRight.value = "n/a";
      if (this.prismVerticalBaseRight) {
        this.prismVerticalBaseRight.value = "n/a";
        this.prismVerticalBaseRight.disabled = true;
      }
      if (this.prismHorizontalRight) this.prismHorizontalRight.value = "n/a";
      if (this.prismHorizontalBaseRight) {
        this.prismHorizontalBaseRight.value = "n/a";
        this.prismHorizontalBaseRight.disabled = true;
      }
      if (this.prismVerticalLeft) this.prismVerticalLeft.value = "n/a";
      if (this.prismVerticalBaseLeft) {
        this.prismVerticalBaseLeft.value = "n/a";
        this.prismVerticalBaseLeft.disabled = true;
      }
      if (this.prismHorizontalLeft) this.prismHorizontalLeft.value = "n/a";
      if (this.prismHorizontalBaseLeft) {
        this.prismHorizontalBaseLeft.value = "n/a";
        this.prismHorizontalBaseLeft.disabled = true;
      }
      this.clearAllPrismErrors();
      this.hidePrismSummaryError();
    } else {
      // Validate when enabled (but don't show errors until Continue is clicked)
      setTimeout(() => this.validatePrismRules(false), 100);
    }
    this.handlePrescriptionUpdate();
  }

  initPrismFieldHandlers() {
    // Initialize base direction field states based on current delta values
    if (this.prismVerticalRight && this.prismVerticalBaseRight) {
      this.handlePrismDeltaChange(this.prismVerticalRight, this.prismVerticalBaseRight);
    }
    if (this.prismHorizontalRight && this.prismHorizontalBaseRight) {
      this.handlePrismDeltaChange(this.prismHorizontalRight, this.prismHorizontalBaseRight);
    }
    if (this.prismVerticalLeft && this.prismVerticalBaseLeft) {
      this.handlePrismDeltaChange(this.prismVerticalLeft, this.prismVerticalBaseLeft);
    }
    if (this.prismHorizontalLeft && this.prismHorizontalBaseLeft) {
      this.handlePrismDeltaChange(this.prismHorizontalLeft, this.prismHorizontalBaseLeft);
    }
  }

  handlePrismDeltaChange(deltaField, baseField) {
    if (!deltaField || !baseField) return;

    const deltaValue = deltaField.value;
    if (deltaValue === "n/a" || deltaValue === "") {
      baseField.disabled = true;
      baseField.value = "n/a";
      this.clearPrismFieldError(baseField);
    } else {
      baseField.disabled = false;
      // Don't auto-fill the base direction - let it remain as user's selection or empty
      // User must manually select the direction when they choose a prism value
    }

    // Don't validate on field change - validation happens on Continue button click
  }

  validatePrismRules(showErrors = false) {
    if (!this.addPrismCheckbox || !this.addPrismCheckbox.checked) {
      this.clearAllPrismErrors();
      return { valid: true };
    }

    const prismVerticalRight = this.prismVerticalRight?.value || "n/a";
    const prismVerticalBaseRight = this.prismVerticalBaseRight?.value || "n/a";
    const prismHorizontalRight = this.prismHorizontalRight?.value || "n/a";
    const prismHorizontalBaseRight = this.prismHorizontalBaseRight?.value || "n/a";
    const prismVerticalLeft = this.prismVerticalLeft?.value || "n/a";
    const prismVerticalBaseLeft = this.prismVerticalBaseLeft?.value || "n/a";
    const prismHorizontalLeft = this.prismHorizontalLeft?.value || "n/a";
    const prismHorizontalBaseLeft = this.prismHorizontalBaseLeft?.value || "n/a";

    // Clear all previous errors when showing new ones
    if (showErrors) {
      this.clearAllPrismErrors();
      this.hidePrismSummaryError();
    }

    const errors = [];

    // Helper function to check if value is "n/a" or empty
    const isEmpty = (value) => value === "n/a" || value === "" || value === null || value === undefined;

    // Helper function to parse numeric value
    const parseValue = (value) => {
      if (isEmpty(value)) return null;
      const num = parseFloat(value);
      return isNaN(num) ? null : num;
    };

    // Helper function to validate value range (0.50 - 8.00)
    const isValidRange = (value) => {
      const num = parseValue(value);
      if (num === null) return false;
      return num >= 0.50 && num <= 8.00;
    };

    // Validate each eye independently
    const validateEye = (eyeName, verticalValue, verticalBase, horizontalValue, horizontalBase) => {
      const eyeErrors = [];

      // Helper to validate a single prism (vertical or horizontal)
      const validatePrism = (value, base, type, baseOptions) => {
        const valueEmpty = isEmpty(value) || value === "n/a";
        const baseEmpty = isEmpty(base) || base === "n/a";
        const valueNum = parseValue(value);

        // If base direction is selected, value is required
        if (!baseEmpty) {
          if (valueEmpty) {
            eyeErrors.push(`A ${type} prism value is required when a base direction is selected for the ${eyeName} eye.`);
            return;
          }
        }

        // If value is selected (and not empty), validate it
        if (!valueEmpty) {
          // Value must be a number
          if (valueNum === null) {
            eyeErrors.push(`${type} prism value must be a number for the ${eyeName} eye.`);
            return;
          }

          // If value is 0, direction must be None
          if (valueNum === 0) {
            if (!baseEmpty) {
              eyeErrors.push(`${type} prism direction must be "n/a" when the value is 0 for the ${eyeName} eye.`);
            }
            return; // No further validation needed for 0
          }

          // If value > 0, direction is required
          if (valueNum > 0 && baseEmpty) {
            eyeErrors.push(`A base direction (${baseOptions}) is required for the ${type} prism on the ${eyeName} eye.`);
          }

          // Validate value range (0.50 - 8.00)
          if (valueNum < 0.50 || valueNum > 8.00) {
            eyeErrors.push(`${type} prism value must be between 0.50 and 8.00 for the ${eyeName} eye.`);
          }
        }
      };

      // Validate vertical prism
      validatePrism(verticalValue, verticalBase, "Vertical", "Up or Down");

      // Validate horizontal prism
      validatePrism(horizontalValue, horizontalBase, "Horizontal", "In or Out");

      // Note: "Up and Down on same eye" and "In and Out on same eye" are prevented by UI (single dropdown per field)
      // Note: Vertical and horizontal prism CAN both be selected for the same eye (this is allowed)

      return eyeErrors;
    };

    // Validate right eye (OD)
    const rightEyeErrors = validateEye(
      "right",
      prismVerticalRight,
      prismVerticalBaseRight,
      prismHorizontalRight,
      prismHorizontalBaseRight
    );

    // Validate left eye (OS)
    const leftEyeErrors = validateEye(
      "left",
      prismVerticalLeft,
      prismVerticalBaseLeft,
      prismHorizontalLeft,
      prismHorizontalBaseLeft
    );

    // Rule: If Add Prism is selected, at least one prism value must be selected
    // Check this FIRST before other validations
    // A value is considered selected if it exists, is not empty, and is not "n/a"
    const hasAnyPrismValue =
      (prismVerticalRight && prismVerticalRight !== "n/a" && prismVerticalRight !== "") ||
      (prismHorizontalRight && prismHorizontalRight !== "n/a" && prismHorizontalRight !== "") ||
      (prismVerticalLeft && prismVerticalLeft !== "n/a" && prismVerticalLeft !== "") ||
      (prismHorizontalLeft && prismHorizontalLeft !== "n/a" && prismHorizontalLeft !== "");

    // Debug: Log values to help diagnose
    if (!hasAnyPrismValue && this.addPrismCheckbox?.checked) {
      console.log('🔍 No prism values detected:', {
        verticalRight: prismVerticalRight,
        horizontalRight: prismHorizontalRight,
        verticalLeft: prismVerticalLeft,
        horizontalLeft: prismHorizontalLeft,
        verticalRightType: typeof prismVerticalRight,
        horizontalRightType: typeof prismHorizontalRight
      });
    }

    if (!hasAnyPrismValue) {
      errors.push({
        type: "no_prism_value",
        message: "Please select at least one prism value (vertical or horizontal) for either eye when Add Prism is enabled."
      });
    } else {
      // Only validate individual fields if at least one value is selected
      rightEyeErrors.forEach(msg => errors.push({ type: "right_eye_error", message: msg }));
      leftEyeErrors.forEach(msg => errors.push({ type: "left_eye_error", message: msg }));

      // Eye Independence Rules: Right and left eyes are independent
      // Each eye may have prism in one direction only, both directions, or no prism at all
      // Values and directions do NOT have to match between eyes
      // No cross-eye validation needed
    }

    // Show summary error if there are validation errors (show only the first error message)
    // But only if showErrors is true
    if (errors.length > 0 && showErrors) {
      const summaryMessage = errors[0].message || "Please correct the prism values";
      this.showPrismSummaryError(summaryMessage);
    } else if (showErrors) {
      this.hidePrismSummaryError();
    }

    return {
      valid: errors.length === 0,
      errors: errors
    };
  }

  showPrismSummaryError(message) {
    if (!this.prismFields) {
      // Try to find it again
      this.prismFields = this.querySelector(".prescription-prism-fields");
      if (!this.prismFields) {
        return;
      }
    }

    // Ensure prism fields are visible
    if (this.prismFields.style.display === 'none') {
      this.prismFields.style.display = 'block';
    }

    // Find the prism container (parent of prism-fields) to insert error at the top
    const prismContainer = this.prismFields.closest('.prescription-table-prism') || this.prismFields.parentElement;

    // Remove existing summary error from anywhere
    let summaryError = document.querySelector(".prism-summary-error");
    if (summaryError) {
      summaryError.remove();
    }

    // Create new error element
    summaryError = document.createElement("div");
    summaryError.className = "prism-summary-error";
    summaryError.style.cssText = `
      background-color: #FEF2F2;
      border: 1px solid #DC2626;
      border-radius: 6px;
      padding: 12px 16px;
      margin: 16px 0;
      font-family: Inter;
      font-size: 13px;
      line-height: 1.5;
      color: #DC2626;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      z-index: 1000;
      width: 100%;
      box-sizing: border-box;
    `;
    summaryError.textContent = message;

    // Insert error right after the checkbox row, before the prism fields
    const checkboxRow = this.querySelector('.prescription-table-row-prism-checkbox');
    if (checkboxRow && checkboxRow.nextSibling) {
      checkboxRow.parentElement.insertBefore(summaryError, checkboxRow.nextSibling);
    } else if (this.prismFields && this.prismFields.parentElement) {
      // Fallback: insert at the beginning of prism fields
      this.prismFields.parentElement.insertBefore(summaryError, this.prismFields);
    } else {
      // Last resort: insert at the beginning of prism fields
      if (this.prismFields.firstChild) {
        this.prismFields.insertBefore(summaryError, this.prismFields.firstChild);
      } else {
        this.prismFields.appendChild(summaryError);
      }
    }
  }

  hidePrismSummaryError() {
    // Find error element anywhere in the document (it might be outside prismFields)
    const summaryError = document.querySelector(".prism-summary-error");
    if (summaryError) {
      summaryError.remove(); // Remove it completely instead of just hiding
    }
  }

  showPrismFieldError(field, message) {
    if (!field) return;

    // Remove existing error styling
    field.classList.remove("prism-field-error");

    // Add error styling
    field.classList.add("prism-field-error");
    field.style.borderColor = "#DC2626";
    field.style.borderWidth = "2px";

    // Add or update error message
    let errorMessage = field.parentElement.querySelector(".prism-error-message");
    if (!errorMessage) {
      errorMessage = document.createElement("div");
      errorMessage.className = "prism-error-message";
      errorMessage.style.color = "#DC2626";
      errorMessage.style.fontSize = "12px";
      errorMessage.style.marginTop = "4px";
      errorMessage.style.fontFamily = "Inter";
      field.parentElement.appendChild(errorMessage);
    }
    errorMessage.textContent = message;
  }

  clearPrismFieldError(field) {
    if (!field) return;

    field.classList.remove("prism-field-error");
    field.style.borderColor = "";
    field.style.borderWidth = "";

    const errorMessage = field.parentElement.querySelector(".prism-error-message");
    if (errorMessage) {
      errorMessage.remove();
    }
  }

  clearAllPrismErrors() {
    const errorFields = this.querySelectorAll(".prism-field-error");
    errorFields.forEach(field => {
      this.clearPrismFieldError(field);
    });

    const errorMessages = this.querySelectorAll(".prism-error-message");
    errorMessages.forEach(msg => msg.remove());

    this.hidePrismSummaryError();
  }

  openPopupForPDInfo() {
    // Create container for the popup
    const popupContainer = document.createElement("div");
    popupContainer.id = "pd-info-popup-container";

    // Add the popup HTML directly since we're using Liquid
    popupContainer.innerHTML = `
      <div class="pd-info-modal">
        <div class="pd-info-modal-overlay"></div>
        <div class="pd-info-modal-content">
          <button class="pd-info-modal-close">&times;</button>
          <h2 class="pd-info-modal-title">Measure Pupil Distance</h2>
          <div class="pd-info-modal-content-wrapper">
            <div class="pd-info-content">
              <p>Pupillary distance (PD for short) is used for proper fitting of prescription glasses. As an adult, your PD never changes - even when your prescription changes. The average pupillary distance (PD) measurement is around 63mm but it can vary from person to person. You can request your PD measurement from your optician when you receive your prescription. You can also easily measure your PD yourself:</p>
              <p class="pd-info-step">Step 1</p>
              <p>Place a ruler with millimeter measurements on your nose, with the starting point centered on the pupil of one eye</p>
              <p class="pd-info-step">Step 2</p>
              <p>Stand in front of a mirror and look straight. Measure the distance from the centre of the first pupil to the centre of the second pupil. A friend can help in reading the measurement but they must be at the same height as you.</p>
              <p class="pd-info-step">Step 3</p>
              <p>The PD value should be accurate so that your prescription glasses are sized correctly for your face; we recommend that you confirm your PD properly by taking the average of 3 measurements.</p>
            </div>
            <div class="pd-info-image-wrapper">
              <video src="https://do6sydhp1s299.cloudfront.net/assets/images/pd-measurement.mp4" autoplay muted loop></video>
            </div>
          </div>
        </div>
      </div>
    `;

    // Add styles
    const style = document.createElement("style");
    style.textContent = `
      .pd-info-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }
      .pd-info-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
      }
      .pd-info-modal-content {
        position: relative;
        background: white;
        padding: 30px;
        border-radius: 8px;
        max-width: 1000px;
        width: 90%;
        height: 80vh;
        display: flex;
        flex-direction: column;
        z-index: 1001;
      }
      .pd-info-modal-content-wrapper {
        display: flex;
        align-items: stretch;
        justify-content: space-between;
        gap: 30px;
        height: 100%;
        overflow: hidden;
      }
      .pd-info-content {
        font-size: 16px;
        line-height: 1.5;
        width: 60%;
        overflow-y: auto;
        padding-right: 20px;
      }
      .pd-info-image-wrapper {
        width: 40%;
        height: 100%;
        background: #f0f0f0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        overflow: hidden;
      }
      .pd-info-image-wrapper video {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .pd-info-modal-close {
        position: absolute;
        top: 10px;
        right: 10px;
        border: none;
        background: none;
        font-size: 24px;
        cursor: pointer;
        padding: 5px;
        line-height: 1;
      }
      .pd-info-modal-title {
        font-family: Inter;
        margin-top: 0;
        margin-bottom: 20px;
        font-size: 24px;
        text-transform: uppercase;
      }
      .pd-info-step {
        font-weight: bold;
        margin-bottom: 10px;
        font-size: 18px;
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(popupContainer);

    // Add close functionality
    const closeBtn = popupContainer.querySelector(".pd-info-modal-close");
    const overlay = popupContainer.querySelector(".pd-info-modal-overlay");

    const closePopup = () => {
      popupContainer.remove();
      style.remove();
    };

    closeBtn.addEventListener("click", closePopup);
    overlay.addEventListener("click", closePopup);

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closePopup();
      }
    });
  }

  handlePrescriptionMerge(isMerged) {
    if (
      !this.leftEyeRow ||
      !this.rightEyeLabel ||
      !this.leftEyeSelects ||
      !this.rightEyeSelects
    ) {
      return;
    }

    if (isMerged) {
      this.leftEyeRow.classList.add("merged");
      this.querySelector(
        ".perscription-table-cell-label-mobile-left"
      ).classList.add("hidden");
      this.querySelector(
        ".prescription-table-header-left-mobile"
      ).classList.add("hidden");
      this.querySelector(".prescription-table-row").style.marginBottom = "0";
      this.querySelector(
        ".perscription-table-cell-label-mobile-right"
      ).textContent = "Right & Left";
      this.querySelector(".prescription-table-cell-label-right").textContent =
        "Right & Left";
      this.leftEyeSelects.forEach((select) => {
        select.removeAttribute("");
      });
      this.leftEyeSelects.forEach((select, index) => {
        if (select && this.rightEyeSelects[index]) {
          select.removeAttribute("required");
          select.value = this.rightEyeSelects[index].value;
          select.disabled = true;
        }
      });
    } else {
      this.leftEyeRow.classList.remove("merged");
      this.querySelector(
        ".perscription-table-cell-label-mobile-left"
      ).classList.remove("hidden");
      this.querySelector(
        ".prescription-table-header-left-mobile"
      ).classList.remove("hidden");
      this.querySelector(".prescription-table-row").style.marginBottom = "20px";
      this.querySelector(
        ".perscription-table-cell-label-mobile-right"
      ).textContent = "Right(OD)";
      this.querySelector(".prescription-table-cell-label-right").textContent =
        "Right(OD)";

      this.leftEyeSelects.forEach((select) => {
        if (select) {
          select.setAttribute("required", "");
          select.value = "";
          select.disabled = false;
        }
      });
    }

    this.handlePrescriptionUpdate();
  }

  handlePrescriptionUpdate() {
    const rightEyeSph = this.getDropdownValue(this.rightEyeSelects[0]);
    const rightEyeCyl = this.getDropdownValue(this.rightEyeSelects[1]);
    const rightEyeAxis = this.getDropdownValue(this.rightEyeSelects[2]);
    const leftEyeSph = this.getDropdownValue(this.leftEyeSelects[0]);
    const leftEyeCyl = this.getDropdownValue(this.leftEyeSelects[1]);
    const leftEyeAxis = this.getDropdownValue(this.leftEyeSelects[2]);
    // Handle PD - check if split PD is enabled
    const isSplitPd = this.splitPdCheckbox?.checked || false;
    let pdValue = "";
    let pdRightValue = "";
    let pdLeftValue = "";

    if (isSplitPd) {
      // Use getDropdownValue for consistency with custom dropdowns
      pdRightValue = this.pdRight ? this.getDropdownValue(this.pdRight) : "";
      pdLeftValue = this.pdLeft ? this.getDropdownValue(this.pdLeft) : "";
    } else {
      // restSelects[1] is the PD select - use getDropdownValue for consistency
      pdValue = this.restSelects[1] ? this.getDropdownValue(this.restSelects[1]) : "";
    }

    // Handle Prism - check if Add Prism is enabled
    const isAddPrism = this.addPrismCheckbox?.checked || false;
    let prismData = null;
    if (isAddPrism) {
      prismData = {
        right: {
          vertical: this.prismVerticalRight?.value || "n/a",
          verticalBase: this.prismVerticalBaseRight?.value || "n/a",
          horizontal: this.prismHorizontalRight?.value || "n/a",
          horizontalBase: this.prismHorizontalBaseRight?.value || "n/a",
        },
        left: {
          vertical: this.prismVerticalLeft?.value || "n/a",
          verticalBase: this.prismVerticalBaseLeft?.value || "n/a",
          horizontal: this.prismHorizontalLeft?.value || "n/a",
          horizontalBase: this.prismHorizontalBaseLeft?.value || "n/a",
        },
      };
    }

    const formData = {
      right: {
        SPH: rightEyeSph,
        CYL: rightEyeCyl,
        AXI: rightEyeAxis,
      },
      left: {
        SPH: this.merged ? rightEyeSph : leftEyeSph,
        CYL: this.merged ? rightEyeCyl : leftEyeCyl,
        AXI: this.merged ? rightEyeAxis : leftEyeAxis,
      },
      ADD: this.restSelects[0]?.value || "",
      PD: pdValue,
      PD_right: pdRightValue,
      PD_left: pdLeftValue,
      isSplitPd: isSplitPd,
      isSamePrescription: this.mergeCheckbox?.checked || false,
      prism: prismData,
      isAddPrism: isAddPrism,
    };

    const event = new CustomEvent("prescriptionUpdated", {
      detail: formData,
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  getDropdownValue(el) {
    if (!el) return "";
    if (el.tagName === "SELECT") {
      return el.selectedOptions[0]?.value || "";
    }
    // For custom dropdown: look for the hidden input
    const hidden = el.querySelector('input[type="hidden"]');
    return hidden ? hidden.value : "";
  }
}

class RxConfiguratorLoader extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector("button");
    this.configurator = document.querySelector("rx-configurator");
    this.backButton = this.configurator.querySelector(".back-to-product");
    this.backButtonMobile = this.configurator.querySelector(
      ".back-to-product-mobile"
    );
    this.addEventListener("click", () => this.toggleConfigurator());

    // if (this.button && this.configurator) {
    //   this.button.addEventListener("click", () => this.toggleConfigurator());
    // }

    if (this.backButton) {
      this.backButton.addEventListener("click", (e) => {
        e.preventDefault(); // Prevent default link behavior
        this.hideConfigurator();
      });
    }
    if (this.backButtonMobile) {
      this.backButtonMobile.addEventListener("click", (e) => {
        e.preventDefault(); // Prevent default link behavior
        this.hideConfigurator();
      });
    }
  }

  toggleConfigurator() {
    if (this.classList.contains("disabled")) return;
    // Hide Klaviyo
    const teaserContainer = document
      .querySelector('[data-testid="animated-teaser"]')
      ?.closest('div[tabindex="0"]');
    // Show spinner
    this.button.querySelector(".btn-text").classList.add("hidden");
    this.button.querySelector(".loading-spinner").classList.remove("hidden");
    this.configuratorContainer = document.querySelector(".rx-configurator");
    this.configuratorContainer
      .querySelector("rx-configurator")
      .updateToSelectedVariantData();
    document.body.style.overflow = "hidden";
    // Disable button during load
    this.button.disabled = true;

    // Simple timeout to simulate loading
    setTimeout(() => {
      if (teaserContainer) {
        teaserContainer.style.display = "none";
      }
      // Hide button container and show configurator
      this.configuratorContainer.classList.remove("hidden");
      this.configuratorContainer
        .querySelector("rx-configurator")
        .classList.remove("hidden");
      this.button.disabled = false;
      this.button.querySelector(".btn-text").classList.remove("hidden");
      this.button.querySelector(".loading-spinner").classList.add("hidden");
    }, 1500);
  }

  hideConfigurator() {
    // Show Klaviyo
    const teaserContainer = document
      .querySelector('[data-testid="animated-teaser"]')
      ?.closest('div[tabindex="0"]');
    if (teaserContainer) {
      teaserContainer.style.display = "block";
    }
    document.body.style.overflow = "auto";
    this.configuratorContainer.classList.add("hidden");
    this.configuratorContainer
      .querySelector("rx-configurator")
      .classList.add("hidden");
  }
}

class ColorPickerRx extends HTMLElement {
  constructor() {
    super();
    this.classList.add("hidden");
    this.querySelectorAll(".lens-material-color-option").forEach((button) => {
      button.addEventListener("click", this.handleColorChange.bind(this));
    });
    this.selectedColorButton = this.querySelector(".rx-selected_color");

    // Initialize tint darkness slider if it exists
    this.tintSlider = this.querySelector(".tint-darkness-slider");
    if (this.tintSlider) {
      this.darknessValue = this.querySelector(".darkness-value");
      this.tintSlider.addEventListener(
        "input",
        this.handleTintDarknessChange.bind(this)
      );
    }

    // Initialize preset buttons
    this.initPresetButtons();
  }

  initPresetButtons() {
    const presetButtons = this.querySelectorAll(".tint-preset-btn");
    presetButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        const darkness = parseInt(button.dataset.darkness);
        this.setTintDarkness(darkness);
      });
    });

    // Set initial active state
    if (this.tintSlider) {
      const currentDarkness = parseInt(this.tintSlider.value) || 80;
      this.updatePresetButtonStates(currentDarkness);
    }
  }

  setTintDarkness(darkness) {
    if (this.tintSlider) {
      this.tintSlider.value = darkness;
      this.handleTintDarknessChange({ target: this.tintSlider });
    }
  }

  updatePresetButtonStates(darkness) {
    const presetButtons = this.querySelectorAll(".tint-preset-btn");
    presetButtons.forEach((button) => {
      const buttonDarkness = parseInt(button.dataset.darkness);
      if (buttonDarkness === parseInt(darkness)) {
        button.classList.add("active");
      } else {
        button.classList.remove("active");
      }
    });
  }

  open() {
    this.classList.remove("hidden");

    // Auto-select if there's only one color swatch
    const colorOptions = this.querySelectorAll(".lens-material-color-option");
    if (colorOptions.length === 1 && !this.selectedColorButton) {
      // Auto-select the single swatch
      const singleSwatch = colorOptions[0];
      if (singleSwatch) {
        // Remove any existing selection
        this.querySelectorAll(".lens-material-color-option").forEach((button) => {
          button.classList.remove("rx-selected_color");
        });
        // Select the single swatch
        singleSwatch.classList.add("rx-selected_color");
        this.selectedColorButton = singleSwatch;

        // Update header title
        const headerTitle = this.querySelector(
          ".lens-material-color-options-wrapper-header-title"
        );
        if (headerTitle && singleSwatch.dataset.label) {
          headerTitle.textContent = singleSwatch.dataset.label;
        }

        // Trigger the color change handler to update formData
        this.handleColorChange({ currentTarget: singleSwatch });
      }
    } else if (colorOptions.length > 1 && !this.selectedColorButton) {
      // If multiple swatches but none selected, select the first one (with rx-selected_color class)
      const firstSelected = this.querySelector(".rx-selected_color");
      if (firstSelected) {
        this.selectedColorButton = firstSelected;
        // Trigger update to ensure formData is set
        const configurator = document.querySelector("rx-configurator");
        if (configurator) {
          const addOnElement = this.closest("rx-add-on");
          if (addOnElement && addOnElement.selectedAddon) {
            const formData = addOnElement.getSelectedAddOnFormData();
            if (formData) {
              Object.keys(formData).forEach((key) => {
                configurator.formData.lensSpecification[key] = formData[key];
              });
              configurator.updateFooterSelections();
              configurator.updateTotalPrice();
            }
          }
        }
      }
    }
  }

  hide() {
    this.classList.add("hidden");
  }

  handleColorChange(event) {
    this.querySelectorAll(".lens-material-color-option").forEach((button) => {
      button.classList.remove("rx-selected_color");
    });
    event.currentTarget.classList.add("rx-selected_color");
    this.selectedColorButton = event.currentTarget;
    const headerTitle = this.querySelector(
      ".lens-material-color-options-wrapper-header-title"
    );
    if (headerTitle) {
      headerTitle.textContent = event.currentTarget.dataset.label;
    }

    // Update tint darkness if slider exists
    if (this.tintSlider) {
      this.updateTintDarkness();
    }

    const configurator = document.querySelector("rx-configurator");
    if (configurator) {
      const addOnElement = this.closest("rx-add-on");
      if (addOnElement && addOnElement.selectedAddon) {
        const formData = addOnElement.getSelectedAddOnFormData();
        if (formData) {
          Object.keys(formData).forEach((key) => {
            configurator.formData.lensSpecification[key] = formData[key];
          });
          configurator.updateFooterSelections();
          configurator.updateTotalPrice();
        }
      }
    }
  }

  handleTintDarknessChange(event) {
    if (this.darknessValue) {
      this.darknessValue.textContent = `${event.target.value}%`;
    }
    this.updateTintDarkness();
    this.updatePresetButtonStates(event.target.value);

    const configurator = document.querySelector("rx-configurator");
    if (configurator) {
      const addOnElement = this.closest("rx-add-on");
      if (addOnElement && addOnElement.selectedAddon) {
        const formData = addOnElement.getSelectedAddOnFormData();
        if (formData) {
          Object.keys(formData).forEach((key) => {
            configurator.formData.lensSpecification[key] = formData[key];
          });
          configurator.updateFooterSelections();
          configurator.updateTotalPrice();
        }
      }
    }
  }

  updateTintDarkness() {
    if (!this.tintSlider || !this.selectedColorButton) return;

    const darkness = this.tintSlider.value;
    const baseColor = this.selectedColorButton.style.backgroundColor;
  }
}
function showConfirmationModal() {
  return new Promise((resolve) => {
    const modal = document.getElementById("confirmation-modal");
    const yesBtn = document.getElementById("confirm-yes");
    const noBtn = document.getElementById("confirm-no");

    modal.style.display = "block";

    const handleYes = () => {
      cleanup();
      resolve(true);
    };

    const handleNo = () => {
      cleanup();
      resolve(false);
    };

    const cleanup = () => {
      modal.style.display = "none";
      yesBtn.removeEventListener("click", handleYes);
      noBtn.removeEventListener("click", handleNo);
    };

    yesBtn.addEventListener("click", handleYes);
    noBtn.addEventListener("click", handleNo);
  });
}
customElements.define("color-picker-rx", ColorPickerRx);
customElements.define("rx-configurator-loader", RxConfiguratorLoader);
customElements.define("rx-configurator", RxConfigurator);
customElements.define("rx-prescription", PrescriptionEntry);
customElements.define("rx-add-on", LensMaterialSelector);
